import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Input,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CardHeader,
  CardFooter,
} from 'reactstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { db } from '../../../firebase';
import { doc, getDoc, getDocs, updateDoc, serverTimestamp, collection, query, where } from 'firebase/firestore';
import { customDropdownWithBorder } from '../../../util/reactCustomStyles';
import { getCollectionNameForSpecialty } from '../../../util/specialtyCollections';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from '../../../redux/QuestionBank/QuestionsAction';
import { fetchPassages } from '../../../redux/QuestionBank/PassageAction';
import { saveTestProgress, fetchTest, fetchUserAnswerHistory, clearCurrentTest, fetchUserPerformance } from '../../../redux/QuestionBank/TestProgressAction';
import { draftRawToHtml } from '../../../util/draftToHtml';
import Header from 'components/Headers/Header'
import QuestionFeedbackModal from '../../../components/QuestionBank/QuestionFeedbackModal';
import AccessRestriction from '../../../components/AccessRestriction/AccessRestriction';
import TutoredModeAIChat from '../../../components/QuestionBank/TutoredModeAIChat';
import TrialBanner from '../../../components/Trial/TrialBanner';
import BoardsCountdownBanner from '../../../components/Trial/BoardsCountdownBanner';
import AddonCrossSellBanner from '../../../components/Billing/AddonCrossSellBanner';
import { getSpecialty } from '../../../config/specialtyConfig';
import { getDisplayName } from 'util/specialtyDisplayHelper';
import ActivityStreaksCard from '../../../components/QuestionBank/ActivityStreaksCard';

const CreateTest = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { questions } = useSelector(state => state.questionBankQuestions);
  const { passages } = useSelector(state => state.questionBankPassages);
  const userHandle = useSelector(state => state.loginAuth?.userDetails?.handle);
  const userName = useSelector(state => state.loginAuth?.userDetails?.name);
  const userDetails = useSelector(state => state.loginAuth?.userDetails);
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);
  const specialtyName = getSpecialty(activeSpecialty).name;

  const [isTutor, setIsTutor] = useState(true);
  const [isTimed, setIsTimed] = useState(false);
  const [timeAccommodation, setTimeAccommodation] = useState(1.0);
  const [selectionMode, setSelectionMode] = useState('standard');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [expandedUnits, setExpandedUnits] = useState([]); // Controls which units show topics (separate from selection)
  const [questionStatus, setQuestionStatus] = useState({
    unused: true,
    used: false,
    incorrect: false,
    marked: false,
    omitted: false,
    correct: false
  });
  const [questionQuality, setQuestionQuality] = useState({
    excellent: true,
    simple: true
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearchInput, setTagSearchInput] = useState('');
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const tagDropdownRef = useRef(null);
  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const maxQuestionsPerBlock = 60;
  const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
  const [testId, setTestId] = useState('');
  const [customQuestionIds, setCustomQuestionIds] = useState('');
  const [retrievingQuestions, setRetrievingQuestions] = useState(false);
  const [inProgressQuestionIds, setInProgressQuestionIds] = useState(new Set());

  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testQuestions, setTestQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [submittedQuestions, setSubmittedQuestions] = useState([]);
  const [testLoaded, setTestLoaded] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0); // For untimed tests - counts up
  const [testStartTime, setTestStartTime] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  // Markup tools state
  const [markupMode, setMarkupMode] = useState(null); // 'highlight' or 'strikethrough'
  const [highlightColor, setHighlightColor] = useState('#ffff00'); // yellow default
  const [annotations, setAnnotations] = useState({}); // Store annotations per question

  // Font size zoom state
  const [fontSizePercent, setFontSizePercent] = useState(120);

  // Mobile responsive state
  const [isMobilePassageVisible, setIsMobilePassageVisible] = useState(true);
  const [mobilePassageHeight, setMobilePassageHeight] = useState(300); // Default height for resizable passage

  // Handle drag resize for mobile passage
  const handlePassageResizeStart = (e) => {
    e.preventDefault();
    const startY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    const startHeight = mobilePassageHeight;

    const handleMove = (moveEvent) => {
      const currentY = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const diff = currentY - startY;
      const newHeight = Math.min(Math.max(startHeight + diff, 150), window.innerHeight * 0.7);
      setMobilePassageHeight(newHeight);
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);
  };

  // Test progress state
  const [savedTestId, setSavedTestId] = useState(null);
  const [testName, setTestName] = useState('');
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [timeSpentTracking, setTimeSpentTracking] = useState(0);
  const [questionTimeTracking, setQuestionTimeTracking] = useState({}); // Track time per question {questionId: seconds}
  const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(null); // When current question started
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false); // Feedback/errata modal
  const [reviewFilter, setReviewFilter] = useState('all'); // 'all', 'incomplete', 'flagged'
  const [isNavigatorModalOpen, setIsNavigatorModalOpen] = useState(false);
  const [isPeriodicTableOpen, setIsPeriodicTableOpen] = useState(false);
  const [testLayoutMode, setTestLayoutMode] = useState('standard'); // 'standard' or 'pmr'
  const [showPmrReview, setShowPmrReview] = useState(false); // Show review section in PM&R layout
  const [hasSeenPmrReview, setHasSeenPmrReview] = useState(false); // Track if user has seen the review screen
  const [pmrInstructionsOpen, setPmrInstructionsOpen] = useState(true); // Collapse state for instructions
  const [pmrQuestionsOpen, setPmrQuestionsOpen] = useState(true); // Collapse state for questions
  const [sortBy, setSortBy] = useState(null); // 'status' or 'flagged'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [isSubmitting, setIsSubmitting] = useState(false); // H1: double-click guard
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [canScrollDown, setCanScrollDown] = useState(false); // Scroll indicator for answer choices
  const [hasPendingChanges, setHasPendingChanges] = useState(false); // Track unsaved changes
  const saveStatusTimeoutRef = useRef(null); // For auto-clearing "saved" status
  const [isIdleModalOpen, setIsIdleModalOpen] = useState(false); // Idle detection modal
  const [isPaused, setIsPaused] = useState(false); // Pause time tracking when idle
  const [pauseReason, setPauseReason] = useState('idle'); // 'idle' or 'tab' - reason for pause
  const lastActivityTimeRef = useRef(Date.now()); // Track last user activity
  const idleCheckIntervalRef = useRef(null); // Interval to check for idle
  const testModalRef = useRef(null); // Ref to test modal for activity tracking

  // Refs for latest state values — prevents stale closures in timer/auto-save (C1, H8)
  const selectedAnswersRef = useRef(selectedAnswers);
  const questionTimeTrackingRef = useRef(questionTimeTracking);
  const currentQuestionStartTimeRef = useRef(currentQuestionStartTime);
  const testQuestionsRef = useRef(testQuestions);
  const currentQuestionIndexRef = useRef(currentQuestionIndex);
  const submittedQuestionsRef = useRef(submittedQuestions);
  const markedQuestionsRef = useRef(markedQuestions);
  const annotationsRef = useRef(annotations);
  const timeSpentTrackingRef = useRef(timeSpentTracking);
  const questionContentRef = useRef(null);
  const passageContentRef = useRef(null);
  const elapsedTimeRef = useRef(elapsedTime);
  const timeRemainingRef = useRef(timeRemaining);
  const autoSubmitTriggeredRef = useRef(false);
  const savedTestIdRef = useRef(savedTestId);
  const testNameRef = useRef(testName);
  const isReviewModeRef = useRef(isReviewMode);
  const autoSaveIntervalRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => { selectedAnswersRef.current = selectedAnswers; }, [selectedAnswers]);
  useEffect(() => { questionTimeTrackingRef.current = questionTimeTracking; }, [questionTimeTracking]);
  useEffect(() => { currentQuestionStartTimeRef.current = currentQuestionStartTime; }, [currentQuestionStartTime]);
  useEffect(() => { testQuestionsRef.current = testQuestions; }, [testQuestions]);
  useEffect(() => { currentQuestionIndexRef.current = currentQuestionIndex; }, [currentQuestionIndex]);
  useEffect(() => { submittedQuestionsRef.current = submittedQuestions; }, [submittedQuestions]);
  useEffect(() => { markedQuestionsRef.current = markedQuestions; }, [markedQuestions]);
  useEffect(() => { annotationsRef.current = annotations; }, [annotations]);
  useEffect(() => { timeSpentTrackingRef.current = timeSpentTracking; }, [timeSpentTracking]);
  useEffect(() => { elapsedTimeRef.current = elapsedTime; }, [elapsedTime]);
  useEffect(() => { timeRemainingRef.current = timeRemaining; }, [timeRemaining]);
  useEffect(() => { savedTestIdRef.current = savedTestId; }, [savedTestId]);
  useEffect(() => { testNameRef.current = testName; }, [testName]);
  useEffect(() => { isReviewModeRef.current = isReviewMode; }, [isReviewMode]);

  // Detect when content overflows and show scroll indicator
  useEffect(() => {
    const el = questionContentRef.current;
    if (!el) return;
    const checkScroll = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight;
      const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
      setCanScrollDown(hasOverflow && !isNearBottom);
    };
    const timer = setTimeout(checkScroll, 100);
    el.addEventListener('scroll', checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      el.removeEventListener('scroll', checkScroll);
      observer.disconnect();
    };
  }, [currentQuestionIndex, testLayoutMode, showExplanation, showPmrReview]);

  // Scroll question (and passage) content to top whenever the question changes
  useEffect(() => {
    if (!isTestModalOpen) return;
    questionContentRef.current?.scrollTo({ top: 0 });
    passageContentRef.current?.scrollTo({ top: 0 });
  }, [currentQuestionIndex]);

  const loadInProgressQuestionIds = async () => {
    if (!userHandle) return;
    try {
      const testsRef = collection(db, getCollectionNameForSpecialty('testProgress', activeSpecialty));
      const q = query(
        testsRef,
        where('userHandle', '==', userHandle),
        where('status', 'in', ['in_progress', 'paused'])
      );
      const snapshot = await getDocs(q);
      const ids = new Set();
      snapshot.docs.forEach(docSnap => {
        const tqs = docSnap.data().testQuestions || [];
        tqs.forEach(tq => { if (tq.questionId) ids.add(tq.questionId); });
      });
      setInProgressQuestionIds(ids);
    } catch (error) {
      console.error('Failed to fetch in-progress question IDs:', error);
    }
  };

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions());
    dispatch(fetchPassages());
    if (userHandle) {
      dispatch(fetchUserAnswerHistory());
      dispatch(fetchUserPerformance());
      loadInProgressQuestionIds();
    }
  }, [dispatch, userHandle, activeSpecialty]);


  // Clear Redux currentTest and reset local state when component mounts or when navigating to a different test
  useEffect(() => {
    const resumeTestId = location.state?.resumeTestId;
    const reviewMode = location.state?.reviewMode;

    // Clear previous test data from Redux to prevent stale data
    dispatch(clearCurrentTest());

    // Reset local state for fresh start
    setTestLoaded(false);
    setSavedTestId(null);
    setIsReviewMode(false);

    if (resumeTestId) {
      Swal.fire({
        title: reviewMode ? 'Loading Review...' : 'Resuming Test...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });
      dispatch(fetchTest(resumeTestId));
      setIsReviewMode(reviewMode || false);
      setSavedTestId(resumeTestId);
    }

    // Cleanup when leaving the component
    return () => {
      dispatch(clearCurrentTest());
    };
  }, [location.state?.resumeTestId, location.state?.reviewMode, dispatch]);

  // Load saved test data when it's fetched
  const { currentTest, userAnswerHistory, userPerformance } = useSelector(state => state.testProgress);
  const cachedTestSummaries = userPerformance?.testSummaries || [];
  useEffect(() => {
    // CRITICAL: Validate that currentTest matches the savedTestId to prevent loading stale data
    if (currentTest && savedTestId && !testLoaded && currentTest.testId === savedTestId) {
      // Log for debugging
      console.log('Loading test:', currentTest.testId, 'Status:', currentTest.status, 'Answers:', Object.keys(currentTest.selectedAnswers || {}).length);

      // Merge fresh question analytics (timesAnswered, timesCorrect) from current questions data
      const savedQuestions = currentTest.testQuestions || [];
      const updatedTestQuestions = savedQuestions.map(savedQ => {
        // Find fresh question data from Redux store
        const freshQuestion = questions?.find(q => q.questionId === savedQ.questionId);
        if (freshQuestion) {
          return {
            ...savedQ,
            timesAnswered: freshQuestion.timesAnswered || 0,
            timesCorrect: freshQuestion.timesCorrect || 0,
            averageTimeSpent: freshQuestion.averageTimeSpent || 0,
            answerDistribution: freshQuestion.answerDistribution || savedQ.answerDistribution || {},
          };
        }
        return savedQ;
      });

      // Restore test state
      setTestName(currentTest.testName);
      setTestQuestions(updatedTestQuestions);
      setCurrentQuestionIndex(isReviewMode ? 0 : (currentTest.currentQuestionIndex || 0));
      setSelectedAnswers(currentTest.selectedAnswers || {});
      setMarkedQuestions(currentTest.markedQuestions || []);
      setSubmittedQuestions(currentTest.submittedQuestions || []);
      setAnnotations(currentTest.annotations || {});
      setTimeSpentTracking(currentTest.timeSpent || 0);
      timeSpentTrackingRef.current = currentTest.timeSpent || 0;
      setIsTimed(currentTest.isTimed || false);
      setIsTutor(currentTest.isTutor !== undefined ? currentTest.isTutor : true);

      // Handle timeRemaining based on timed/untimed setting
      const timeAccom = currentTest.timeAccommodation || 1.0;
      let calculatedTimeRemaining = null;

      if (currentTest.isTimed) {
        if (currentTest.timeRemaining !== null && currentTest.timeRemaining !== undefined && currentTest.timeRemaining > 0) {
          // Use saved time remaining
          calculatedTimeRemaining = currentTest.timeRemaining;
        } else {
          // Calculate time based on questions and time already spent (changed from untimed to timed)
          const baseTimePerQuestion = 66; // seconds
          const totalTime = Math.round(updatedTestQuestions.length * baseTimePerQuestion * timeAccom);
          const timeAlreadySpent = currentTest.timeSpent || 0;
          calculatedTimeRemaining = Math.max(totalTime - timeAlreadySpent, 60); // At least 1 minute
        }
      }
      // If not timed, calculatedTimeRemaining stays null
      setTimeRemaining(calculatedTimeRemaining);

      // Load elapsed time for untimed tests
      if (!currentTest.isTimed && currentTest.elapsedTime !== undefined) {
        setElapsedTime(currentTest.elapsedTime || 0);
      }

      setTimeAccommodation(timeAccom);
      setQuestionTimeTracking(currentTest.questionTimeTracking || {});
      setSelectionMode(currentTest.selectionMode || 'standard');

      // Check if the current question was already submitted (tutor mode)
      const restoredSubmitted = currentTest.submittedQuestions || [];
      const restoredIndex = currentTest.currentQuestionIndex || 0;
      const currentQId = updatedTestQuestions[restoredIndex]?.questionId;
      const isTutorMode = currentTest.isTutor !== undefined ? currentTest.isTutor : true;
      const isCurrentSubmitted = isTutorMode && currentQId && restoredSubmitted.includes(currentQId);

      // If resuming on a submitted question, show explanation and pause timer
      if (isCurrentSubmitted) {
        setShowExplanation(true);
        setCurrentQuestionStartTime(null);
        setIsPaused(true);
      } else {
        setCurrentQuestionStartTime(Date.now());
      }

      // Open test modal
      Swal.close();
      setIsTestModalOpen(true);
      setTestStartTime(Date.now());
      setTestLoaded(true);
    }
  // Note: Removed 'questions' from dependencies to prevent race conditions when resuming tests.
  // The questions array is only used for merging analytics data, not for triggering the load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTest, savedTestId, testLoaded]);

  // Auto-save test progress every 30 seconds (only for in-progress tests, not review mode)
  // Uses refs to always read latest state, preventing stale closure bugs (H8)
  useEffect(() => {
    if (!isTestModalOpen || !savedTestId || isReviewMode) return;

    autoSaveIntervalRef.current = setInterval(() => {
      // Read latest values from refs to avoid stale closures
      let updatedTimeTracking = { ...questionTimeTrackingRef.current };
      const startTime = currentQuestionStartTimeRef.current;
      const tq = testQuestionsRef.current;
      const idx = currentQuestionIndexRef.current;
      const submitted = submittedQuestionsRef.current;

      // Only update time if there's an active timer AND question is not already submitted
      if (startTime && tq[idx]) {
        const questionId = tq[idx].questionId;
        const isSubmitted = submitted.includes(questionId);

        if (!isSubmitted) {
          const timeSpent = Math.floor((Date.now() - startTime) / 1000);
          updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
          // Reset start time to prevent double-counting on next auto-save or navigation
          setCurrentQuestionStartTime(Date.now());
          setQuestionTimeTracking(updatedTimeTracking);
        }
      }
      handleSaveProgress({ questionTimeTracking: updatedTimeTracking });
    }, 10000); // 10 seconds

    return () => {
      if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestModalOpen, savedTestId, isReviewMode]);

  // Warn user before closing/refreshing browser during an active test AND trigger auto-save (H6)
  useEffect(() => {
    if (!isTestModalOpen || isReviewMode) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';

      // Best-effort save using refs ONLY — avoids stale closure bugs (H6)
      // Direct Firestore updateDoc (synchronous intent, best-effort) since Redux dispatch is async and unreliable here
      if (savedTestIdRef.current && testQuestionsRef.current?.length > 0) {
        let updatedTimeTracking = { ...questionTimeTrackingRef.current };
        const startTime = currentQuestionStartTimeRef.current;
        const tq = testQuestionsRef.current;
        const idx = currentQuestionIndexRef.current;
        const submitted = submittedQuestionsRef.current;
        if (startTime && tq[idx]) {
          const questionId = tq[idx].questionId;
          const isSubmitted = submitted.includes(questionId);
          if (!isSubmitted) {
            const timeSpent = Math.floor((Date.now() - startTime) / 1000);
            updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
          }
        }
        // Direct Firestore write — all values from refs to avoid stale closures
        try {
          const testRef = doc(db, getCollectionNameForSpecialty('testProgress', activeSpecialty), savedTestIdRef.current);
          updateDoc(testRef, {
            status: 'in_progress',
            currentQuestionIndex: idx,
            selectedAnswers: selectedAnswersRef.current,
            markedQuestions: markedQuestionsRef.current,
            submittedQuestions: submitted,
            timeSpent: timeSpentTrackingRef.current,
            timeRemaining: timeRemainingRef.current,
            elapsedTime: elapsedTimeRef.current || 0,
            questionTimeTracking: updatedTimeTracking,
            updatedAt: Date.now(),
          });
        } catch (_) {
          // best-effort — browser may still cancel it, nothing we can do
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestModalOpen, isReviewMode]);

  // Track time spent via ref to avoid re-rendering the entire component every second.
  // The ref is synced to state only when needed (save, submit, close).
  // Time tracking pauses when isPaused is true (user is idle)
  const timeSpentIntervalRef = useRef(null);
  const isPausedRef = useRef(isPaused);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  useEffect(() => {
    if (!isTestModalOpen || isReviewMode) {
      if (timeSpentIntervalRef.current) {
        clearInterval(timeSpentIntervalRef.current);
        timeSpentIntervalRef.current = null;
      }
      return;
    }

    timeSpentIntervalRef.current = setInterval(() => {
      // Only increment time if not paused (user is active)
      if (!isPausedRef.current) {
        timeSpentTrackingRef.current = (timeSpentTrackingRef.current || 0) + 1;
      }
    }, 1000);

    return () => {
      if (timeSpentIntervalRef.current) {
        clearInterval(timeSpentIntervalRef.current);
        timeSpentIntervalRef.current = null;
      }
    };
  }, [isTestModalOpen, isReviewMode]);

  // Idle detection - pause time tracking after 5 minutes of no interaction OR when leaving tab
  useEffect(() => {
    if (!isTestModalOpen || isReviewMode) {
      // Clear idle check when test modal closes
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
      return;
    }

    // Reset activity time when test starts
    lastActivityTimeRef.current = Date.now();

    // Helper function to pause and save current question time
    const pauseTimeTracking = (reason = 'idle') => {
      if (isPausedRef.current) return; // Already paused

      setIsPaused(true);
      setPauseReason(reason);

      // Save current question time before pausing
      if (currentQuestionStartTimeRef.current && testQuestionsRef.current[currentQuestionIndexRef.current]) {
        const questionId = testQuestionsRef.current[currentQuestionIndexRef.current].questionId;
        const submitted = submittedQuestionsRef.current;
        const isSubmitted = submitted.includes(questionId);

        if (!isSubmitted) {
          const timeSpent = Math.floor((Date.now() - currentQuestionStartTimeRef.current) / 1000);
          setQuestionTimeTracking(prev => ({
            ...prev,
            [questionId]: (prev[questionId] || 0) + timeSpent
          }));
        }
        // Clear start time since we're pausing
        setCurrentQuestionStartTime(null);
      }

      // Show idle modal
      setIsIdleModalOpen(true);
    };

    // Track user activity events
    const handleActivity = () => {
      lastActivityTimeRef.current = Date.now();
      // If user was idle and modal is showing, this will be handled by resume button
    };

    // Handle tab visibility change - pause immediately when leaving tab
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched to another tab - pause immediately
        pauseTimeTracking('tab');
      }
    };

    // Events that count as user activity (only within test modal)
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Attach to test modal element if available, otherwise use document
    // Capture the element used for attach so cleanup targets the same element
    let attachedElement = null;

    // We need to reattach when modal element becomes available
    const attachListeners = () => {
      attachedElement = testModalRef.current || document;
      activityEvents.forEach(event => {
        attachedElement.addEventListener(event, handleActivity);
      });
    };

    // Use a small delay to ensure modal is rendered
    const attachTimeout = setTimeout(attachListeners, 100);

    // Listen for tab visibility changes (always on document)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check for idle every 10 seconds (for when user stays on tab but doesn't interact)
    idleCheckIntervalRef.current = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;

      // If idle for 5 minutes (300000ms) and not already paused
      if (timeSinceLastActivity >= IDLE_TIMEOUT && !isPausedRef.current) {
        pauseTimeTracking();
      }
    }, 10000); // Check every 10 seconds

    return () => {
      clearTimeout(attachTimeout);
      if (attachedElement) {
        activityEvents.forEach(event => {
          attachedElement.removeEventListener(event, handleActivity);
        });
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestModalOpen, isReviewMode]);

  // Handle resuming from idle
  const handleResumeFromIdle = () => {
    setIsIdleModalOpen(false);
    setIsPaused(false);
    lastActivityTimeRef.current = Date.now();

    // Restart question time tracking if not submitted
    const currentQ = testQuestions[currentQuestionIndex];
    if (currentQ) {
      const isSubmitted = isTutor && submittedQuestions.includes(currentQ.questionId);
      if (!isSubmitted) {
        setCurrentQuestionStartTime(Date.now());
      }
    }
  };

  const questionCounts = useMemo(() => {
    if (!questions || questions.length === 0) {
      return {
        bySubject: {},
        byUnit: {},
        byTopic: {},
        byFilter: {
          unused: 0,
          incorrect: 0,
          marked: 0,
          omitted: 0,
          correct: 0
        },
        byQuality: {
          excellent: 0,
          simple: 0
        }
      };
    }

    const bySubject = {};
    const byUnit = {};
    const byTopic = {};
    const byFilter = {
      unused: 0,
      used: 0,
      incorrect: 0,
      marked: 0,
      omitted: 0,
      correct: 0
    };
    const byQuality = {
      excellent: 0,
      simple: 0
    };

    questions.forEach(question => {
      // Apply tag filter — skip questions that don't match selected tags
      if (selectedTags.length > 0) {
        const questionTags = question.tags || [];
        if (!selectedTags.some(tag => questionTags.includes(tag))) return;
      }

      // Skip questions already in an in-progress/paused test
      if (inProgressQuestionIds.has(question.questionId)) return;

      const userAnswer = userAnswerHistory[question.questionId];

      if (!userAnswer) {
        byFilter.unused++;
      } else {
        byFilter.used++;
        if (userAnswer.wasOmitted) {
          byFilter.omitted++;
        } else if (userAnswer.isCorrect) {
          byFilter.correct++;
        } else {
          byFilter.incorrect++;
        }
        if (userAnswer.isMarked) {
          byFilter.marked++;
        }
      }

      // Count by quality
      const quality = question.quality || 'simple';
      if (byQuality.hasOwnProperty(quality)) {
        byQuality[quality]++;
      }

      let shouldCountInSubjectUnitTopic = false;

      if (!userAnswer) {
        if (questionStatus.unused) {
          shouldCountInSubjectUnitTopic = true;
        }
      } else {
        if (questionStatus.used) {
          shouldCountInSubjectUnitTopic = true;
        }
        if (userAnswer.wasOmitted && questionStatus.omitted) {
          shouldCountInSubjectUnitTopic = true;
        } else if (userAnswer.isCorrect && questionStatus.correct) {
          shouldCountInSubjectUnitTopic = true;
        } else if (!userAnswer.isCorrect && !userAnswer.wasOmitted && questionStatus.incorrect) {
          shouldCountInSubjectUnitTopic = true;
        }
        if (userAnswer.isMarked && questionStatus.marked) {
          shouldCountInSubjectUnitTopic = true;
        }
      }

      if (shouldCountInSubjectUnitTopic) {
        if (question.subjectId) {
          if (!bySubject[question.subjectId]) {
            bySubject[question.subjectId] = 0;
          }
          bySubject[question.subjectId]++;
        }

        if (question.unitId) {
          if (!byUnit[question.unitId]) {
            byUnit[question.unitId] = 0;
          }
          byUnit[question.unitId]++;
        }

        if (question.topicId) {
          if (!byTopic[question.topicId]) {
            byTopic[question.topicId] = 0;
          }
          byTopic[question.topicId]++;
        }
      }
    });

    return { bySubject, byUnit, byTopic, byFilter, byQuality };
  }, [questions, questionStatus, userAnswerHistory, selectedTags, inProgressQuestionIds]);

  // Compute all available tags from questions
  const availableTags = useMemo(() => {
    if (!questions || questions.length === 0) return [];
    const tagSet = new Set();
    questions.forEach(q => {
      if (q.tags && Array.isArray(q.tags)) {
        q.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return [...tagSet].sort((a, b) => a.localeCompare(b));
  }, [questions]);

  // Filter available tags based on search input
  const filteredAvailableTags = useMemo(() => {
    const unselected = availableTags.filter(tag => !selectedTags.includes(tag));
    if (!tagSearchInput.trim()) return unselected;
    const search = tagSearchInput.toLowerCase();
    return unselected.filter(tag => tag.toLowerCase().includes(search));
  }, [availableTags, tagSearchInput, selectedTags]);

  const handleAddTagFilter = (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags(prev => [...prev, tag]);
    }
    setTagSearchInput('');
  };

  const handleRemoveTagFilter = (tag) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  // Close tag dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target)) {
        setIsTagDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-select all subjects, units, and topics that have questions on initial load
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  useEffect(() => {
    if (!hasAutoSelected && questions && questions.length > 0 && Object.keys(questionCounts.bySubject).length > 0) {
      const subjectsWithQuestions = Object.keys(questionCounts.bySubject).filter(
        subjectId => questionCounts.bySubject[subjectId] > 0
      );
      const unitsWithQuestions = Object.keys(questionCounts.byUnit).filter(
        unitId => questionCounts.byUnit[unitId] > 0
      );
      const topicsWithQuestions = Object.keys(questionCounts.byTopic).filter(
        topicId => questionCounts.byTopic[topicId] > 0
      );
      if (subjectsWithQuestions.length > 0) {
        setSelectedSubjects(subjectsWithQuestions);
        setSelectedUnits(unitsWithQuestions);
        setSelectedTopics(topicsWithQuestions);
        setHasAutoSelected(true);
      }
    }
  }, [questions, questionCounts.bySubject, questionCounts.byUnit, questionCounts.byTopic, hasAutoSelected]);

  useEffect(() => {
    const validSubjects = selectedSubjects.filter(
      subjectId => (questionCounts.bySubject[subjectId] || 0) > 0
    );
    if (validSubjects.length !== selectedSubjects.length) {
      setSelectedSubjects(validSubjects);
    }
  }, [questionCounts, selectedSubjects]);

  useEffect(() => {
    const validUnits = selectedUnits.filter(
      unitId => (questionCounts.byUnit[unitId] || 0) > 0
    );
    if (validUnits.length !== selectedUnits.length) {
      setSelectedUnits(validUnits);
    }
  }, [questionCounts, selectedUnits]);

  useEffect(() => {
    const validTopics = selectedTopics.filter(
      topicId => (questionCounts.byTopic[topicId] || 0) > 0
    );
    if (validTopics.length !== selectedTopics.length) {
      setSelectedTopics(validTopics);
    }
  }, [questionCounts, selectedTopics]);

  useEffect(() => {
    // Don't reset status filters until questions have loaded
    if (!questions || questions.length === 0) {
      return;
    }

    const newStatus = {
      unused: questionStatus.unused && questionCounts.byFilter.unused > 0,
      used: questionStatus.used && questionCounts.byFilter.used > 0,
      incorrect: questionStatus.incorrect && questionCounts.byFilter.incorrect > 0,
      marked: questionStatus.marked && questionCounts.byFilter.marked > 0,
      omitted: questionStatus.omitted && questionCounts.byFilter.omitted > 0,
      correct: questionStatus.correct && questionCounts.byFilter.correct > 0
    };

    const hasChanged = Object.keys(newStatus).some(
      key => newStatus[key] !== questionStatus[key]
    );

    if (hasChanged) {
      setQuestionStatus(newStatus);
    }
  }, [questionCounts.byFilter, questionStatus, questions]);

  // Group questions into passage groups and standalone questions
  const questionUnits = useMemo(() => {
    if (!questions || questions.length === 0 || !passages) {
      return [];
    }

    const filteredQuestions = questions.filter(question => {
      // Apply tag filter — if tags are selected, question must have at least one matching tag
      if (selectedTags.length > 0) {
        const questionTags = question.tags || [];
        const hasMatchingTag = selectedTags.some(tag => questionTags.includes(tag));
        if (!hasMatchingTag) return false;
      }

      // Exclude questions already in an in-progress/paused test
      if (inProgressQuestionIds.has(question.questionId)) return false;

      const matchesSubjectUnitOrTopic = (() => {
        // If nothing is selected, include all questions
        if (selectedSubjects.length === 0 && selectedUnits.length === 0 && selectedTopics.length === 0) {
          return true;
        }

        // Priority: Topics > Units > Subjects
        // Only use the most specific selection level that applies to this question

        // CASE 1: If this question has a topic and ANY topics are selected globally
        if (question.topicId && selectedTopics.length > 0) {
          // Only include if THIS question's topic is selected
          return selectedTopics.includes(question.topicId);
        }

        // CASE 2: If this question has a unit (but no topic or no topics selected) and ANY units are selected
        if (question.unitId && selectedUnits.length > 0) {
          // Check if there are selected topics for this specific unit
          const unitTopicIds = topics?.filter(t => t.unitId === question.unitId).map(t => t.topicId) || [];
          const hasSelectedTopicsInThisUnit = unitTopicIds.some(tid => selectedTopics.includes(tid));

          // If this unit has selected topics, question must match a topic (handled in CASE 1, so exclude here)
          if (hasSelectedTopicsInThisUnit) {
            return false;
          }

          // No selected topics in this unit, so check if unit is selected
          return selectedUnits.includes(question.unitId);
        }

        // CASE 3: If this question has a subject and ANY subjects are selected
        if (question.subjectId && selectedSubjects.length > 0) {
          // Check if there are selected units for this specific subject
          const subjectUnitIds = units?.filter(u => u.subjectId === question.subjectId).map(u => u.unitId) || [];
          const hasSelectedUnitsInThisSubject = subjectUnitIds.some(uid => selectedUnits.includes(uid));

          // If this subject has selected units, question must match a unit (handled in CASE 2, so exclude here)
          if (hasSelectedUnitsInThisSubject) {
            return false;
          }

          // No selected units in this subject, so check if subject is selected
          return selectedSubjects.includes(question.subjectId);
        }

        return false;
      })();

      if (!matchesSubjectUnitOrTopic) {
        return false;
      }

      const userAnswer = userAnswerHistory[question.questionId];

      if (!userAnswer) {
        return questionStatus.unused;
      } else {
        let shouldInclude = false;
        if (questionStatus.used) {
          shouldInclude = true;
        }
        if (userAnswer.wasOmitted && questionStatus.omitted) {
          shouldInclude = true;
        } else if (userAnswer.isCorrect && questionStatus.correct) {
          shouldInclude = true;
        } else if (!userAnswer.isCorrect && !userAnswer.wasOmitted && questionStatus.incorrect) {
          shouldInclude = true;
        }
        if (userAnswer.isMarked && questionStatus.marked) {
          shouldInclude = true;
        }
        return shouldInclude;
      }
    });

    // Group by passage
    const passageGroups = {};
    const standalone = [];

    filteredQuestions.forEach(question => {
      if (question.passageId) {
        if (!passageGroups[question.passageId]) {
          const passage = passages.find(p => p.passageId === question.passageId);
          passageGroups[question.passageId] = {
            type: 'passage',
            passageId: question.passageId,
            passage: passage,
            questions: []
          };
        }
        passageGroups[question.passageId].questions.push(question);
      } else {
        standalone.push({
          type: 'standalone',
          question: question
        });
      }
    });

    // Return array of units (passage groups + standalone questions)
    return [...Object.values(passageGroups), ...standalone];
  }, [questions, passages, selectedSubjects, selectedUnits, selectedTopics, questionStatus, userAnswerHistory, selectedTags, inProgressQuestionIds]);

  // Legacy name for compatibility
  const availableQuestions = useMemo(() => {
    // Flatten units into questions for backward compatibility
    const allQuestions = [];
    questionUnits.forEach(unit => {
      if (unit.type === 'passage') {
        allQuestions.push(...unit.questions);
      } else {
        allQuestions.push(unit.question);
      }
    });
    return allQuestions;
  }, [questionUnits]);

  // Count actual questions (not units) - passages contribute all their questions
  const dynamicMaxQuestions = useMemo(() => {
    if (selectedSubjects.length === 0 && selectedUnits.length === 0 && selectedTopics.length === 0) {
      return 0;
    }

    // Count actual individual questions
    let totalQuestions = 0;
    questionUnits.forEach(unit => {
      if (unit.type === 'passage') {
        totalQuestions += unit.questions.length;
      } else {
        totalQuestions += 1;
      }
    });

    return Math.min(totalQuestions, maxQuestionsPerBlock);
  }, [questionUnits, selectedSubjects, selectedUnits, selectedTopics, maxQuestionsPerBlock]);

  useEffect(() => {
    if (numberOfQuestions > dynamicMaxQuestions && dynamicMaxQuestions > 0) {
      setNumberOfQuestions(dynamicMaxQuestions);
    }
  }, [dynamicMaxQuestions, numberOfQuestions]);

  const filteredUnits = units;

  const handleSubjectToggle = (subjectId) => {
    // Get all units and topics under this subject that have questions
    const subjectUnitsWithQuestions = units
      .filter(unit => unit.subjectId === subjectId)
      .filter(unit => (questionCounts.byUnit[unit.unitId] || 0) > 0);
    const subjectUnitsIds = subjectUnitsWithQuestions.map(u => u.unitId);

    const subjectTopicsWithQuestions = topics
      ?.filter(topic => topic.subjectId === subjectId)
      .filter(topic => (questionCounts.byTopic?.[topic.topicId] || 0) > 0) || [];
    const subjectTopicsIds = subjectTopicsWithQuestions.map(t => t.topicId);

    // Check if ALL units and topics are currently selected
    const allUnitsSelected = subjectUnitsIds.length > 0 &&
      subjectUnitsIds.every(id => selectedUnits.includes(id));
    const allTopicsSelected = subjectTopicsIds.length === 0 ||
      subjectTopicsIds.every(id => selectedTopics.includes(id));
    const isAllSelected = allUnitsSelected && allTopicsSelected;

    if (isAllSelected) {
      // All selected -> Deselect everything under this subject
      setSelectedSubjects(prev => prev.filter(id => id !== subjectId));
      setSelectedUnits(prev => prev.filter(id => !subjectUnitsIds.includes(id)));
      setSelectedTopics(prev => prev.filter(id => !subjectTopicsIds.includes(id)));

      // Collapse expanded units under this subject
      setExpandedUnits(prev => prev.filter(id => !subjectUnitsIds.includes(id)));
    } else {
      // Not all selected -> Select ALL units and topics under this subject
      setSelectedSubjects(prev => [...new Set([...prev, subjectId])]);
      setSelectedUnits(prev => [...new Set([...prev, ...subjectUnitsIds])]);
      setSelectedTopics(prev => [...new Set([...prev, ...subjectTopicsIds])]);
    }
  };

  const handleUnitToggle = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    const subjectId = unit?.subjectId;

    // Get all topics under this unit that have questions
    const unitTopicsWithQuestions = topics
      ?.filter(topic => topic.unitId === unitId)
      .filter(topic => (questionCounts.byTopic?.[topic.topicId] || 0) > 0) || [];
    const unitTopicsIds = unitTopicsWithQuestions.map(t => t.topicId);

    // Check if ALL topics under this unit are selected AND the unit itself is selected
    const allTopicsSelected = unitTopicsIds.length === 0 ||
      unitTopicsIds.every(id => selectedTopics.includes(id));
    const isUnitInList = selectedUnits.includes(unitId);
    const isAllSelected = isUnitInList && allTopicsSelected;

    if (isAllSelected) {
      // All selected -> Deselect unit and all its topics
      const newSelectedUnits = selectedUnits.filter(id => id !== unitId);
      setSelectedUnits(newSelectedUnits);
      setSelectedTopics(prev => prev.filter(id => !unitTopicsIds.includes(id)));

      // Collapse the unit
      setExpandedUnits(prev => prev.filter(id => id !== unitId));

      // Check if this was the last selected unit from this subject
      // If so, also remove the subject
      if (subjectId) {
        const subjectUnitIds = units?.filter(u => u.subjectId === subjectId).map(u => u.unitId) || [];
        const remainingSelectedUnitsInSubject = newSelectedUnits.filter(id => subjectUnitIds.includes(id));

        if (remainingSelectedUnitsInSubject.length === 0) {
          // No more units selected from this subject, remove the subject too
          setSelectedSubjects(prev => prev.filter(id => id !== subjectId));
        }
      }
    } else {
      // Not all selected -> Select unit and ALL topics under it
      setSelectedUnits(prev => [...new Set([...prev, unitId])]);
      setSelectedTopics(prev => [...new Set([...prev, ...unitTopicsIds])]);

      // Also select the parent subject if not already selected
      if (subjectId && !selectedSubjects.includes(subjectId)) {
        setSelectedSubjects(prev => [...new Set([...prev, subjectId])]);
      }
    }
  };

  const handleTopicToggle = (topicId) => {
    const isCurrentlySelected = selectedTopics.includes(topicId);
    const topic = topics?.find(t => t.topicId === topicId);
    const unitId = topic?.unitId;
    const subjectId = topic?.subjectId;

    if (isCurrentlySelected) {
      // Topic is selected -> Deselect it
      const newSelectedTopics = selectedTopics.filter(id => id !== topicId);
      setSelectedTopics(newSelectedTopics);

      // Check if this was the last selected topic from this unit
      // If so, also remove the unit (to prevent the whole unit from being included)
      if (unitId) {
        const unitTopicIds = topics?.filter(t => t.unitId === unitId).map(t => t.topicId) || [];
        const remainingSelectedTopicsInUnit = newSelectedTopics.filter(id => unitTopicIds.includes(id));

        if (remainingSelectedTopicsInUnit.length === 0) {
          // No more topics selected from this unit, remove the unit too
          const newSelectedUnits = selectedUnits.filter(id => id !== unitId);
          setSelectedUnits(newSelectedUnits);

          // Also check if this was the last selected unit from this subject
          if (subjectId) {
            const subjectUnitIds = units?.filter(u => u.subjectId === subjectId).map(u => u.unitId) || [];
            const remainingSelectedUnitsInSubject = newSelectedUnits.filter(id => subjectUnitIds.includes(id));

            if (remainingSelectedUnitsInSubject.length === 0) {
              // No more units selected from this subject, remove the subject too
              setSelectedSubjects(prev => prev.filter(id => id !== subjectId));
            }
          }
        }
      }
    } else {
      // Topic is not selected -> Select it
      setSelectedTopics(prev => [...new Set([...prev, topicId])]);

      // Also select parent unit and subject if not already selected
      if (unitId && !selectedUnits.includes(unitId)) {
        setSelectedUnits(prev => [...new Set([...prev, unitId])]);
      }
      if (subjectId && !selectedSubjects.includes(subjectId)) {
        setSelectedSubjects(prev => [...new Set([...prev, subjectId])]);
      }
    }
  };

  const handleStatusToggle = (status) => {
    setQuestionStatus(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const handleRetrieveQuestions = async () => {
    if (!testId.trim()) {
      Swal.fire('Error', 'Please enter a test ID or number', 'error');
      return;
    }

    setRetrievingQuestions(true);
    try {
      const collectionName = getCollectionNameForSpecialty('testProgress', activeSpecialty);
      const testsRef = collection(db, collectionName);
      const inputValue = testId.trim();
      let snapshot;

      // Check if input is a TEST-XXXXXXXXX format or a number
      let testNumber;
      if (inputValue.startsWith('TEST-')) {
        // Extract number from TEST-XXXXXXXXX format
        testNumber = parseInt(inputValue.replace('TEST-', ''));
      } else {
        testNumber = parseInt(inputValue);
      }

      if (isNaN(testNumber)) {
        Swal.fire('Error', 'Please enter a valid test ID (TEST-XXXXXXXXX) or number', 'error');
        return;
      }

      const q = query(
        testsRef,
        where('userHandle', '==', userHandle),
        where('testNumber', '==', testNumber)
      );
      snapshot = await getDocs(q);

      if (snapshot.empty) {
        Swal.fire('Error', 'Test not found. Please check the test ID or number.', 'error');
        return;
      }

      const testData = snapshot.docs[0].data();
      const questionCodes = testData.testQuestions?.map(q => q.questionCode).filter(Boolean) || [];

      if (questionCodes.length === 0) {
        Swal.fire('Error', 'No questions found in this test.', 'error');
        return;
      }

      setCustomQuestionIds(questionCodes.join(','));
      Swal.fire('Success', `Retrieved ${questionCodes.length} question IDs`, 'success');
    } catch (error) {
      console.error('Error retrieving test:', error);
      Swal.fire('Error', 'Failed to retrieve test. Please try again.', 'error');
    } finally {
      setRetrievingQuestions(false);
    }
  };

  const validateForm = () => {
    if (selectionMode === 'standard') {
      if (selectedSubjects.length === 0) {
        Swal.fire('Error', 'Please select at least one subject', 'error');
        return false;
      }

      // Check if at least one status is selected
      const hasStatusSelected = Object.values(questionStatus).some(val => val);
      if (!hasStatusSelected) {
        Swal.fire('Error', 'Please select at least one question status filter', 'error');
        return false;
      }

      if (!numberOfQuestions || numberOfQuestions < 1) {
        Swal.fire('Error', 'Number of questions must be at least 1', 'error');
        return false;
      }

      if (availableQuestions.length === 0) {
        Swal.fire('Error', 'No questions available with the selected filters. Please adjust your selection.', 'error');
        return false;
      }

      if (numberOfQuestions > dynamicMaxQuestions) {
        Swal.fire('Error', `Maximum ${dynamicMaxQuestions} questions available`, 'error');
        return false;
      }
    } else {
      // Custom mode
      if (!customQuestionIds.trim()) {
        Swal.fire('Error', 'Please enter question IDs or retrieve them from a test', 'error');
        return false;
      }

      // Validate question IDs format (question codes like QST-BIO-00123 or commas)
      const cleanedIds = customQuestionIds.replace(/\s/g, '');
      if (!/^[A-Za-z0-9\-,]+$/.test(cleanedIds)) {
        Swal.fire('Error', 'Question IDs should only contain letters, numbers, dashes and commas', 'error');
        return false;
      }

      const idArray = cleanedIds.split(',').filter(id => id.trim());
      if (idArray.length === 0) {
        Swal.fire('Error', 'Please enter at least one valid question ID', 'error');
        return false;
      }

      if (idArray.length > maxQuestionsPerBlock) {
        Swal.fire('Error', `Maximum ${maxQuestionsPerBlock} questions allowed per test`, 'error');
        return false;
      }
    }

    return true;
  };

  // Save with retry wrapper - handles status updates and retry logic
  const saveWithRetry = async (saveFunction, maxRetries = 3) => {
    // Clear any existing "saved" status timeout
    if (saveStatusTimeoutRef.current) {
      clearTimeout(saveStatusTimeoutRef.current);
    }

    setSaveStatus('saving');
    setHasPendingChanges(true);

    let lastError = null;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await saveFunction();

        // Check if save was successful
        if (result && result.success !== false) {
          setSaveStatus('saved');
          setHasPendingChanges(false);

          // Auto-clear "saved" status after 3 seconds
          saveStatusTimeoutRef.current = setTimeout(() => {
            setSaveStatus('idle');
          }, 3000);

          return { ...result, success: true };
        } else {
          lastError = result?.error || 'Save failed';
        }
      } catch (error) {
        lastError = error.message || 'Save failed';
        console.error(`Save attempt ${attempt} failed:`, error);
      }

      // Wait before retry (exponential backoff: 1s, 2s, 4s)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
      }
    }

    // All retries failed
    setSaveStatus('error');
    setHasPendingChanges(true);

    // Show error toast
    Swal.fire({
      icon: 'error',
      title: 'Save Failed',
      text: 'Your progress could not be saved. Please check your connection and try again.',
      confirmButtonColor: '#5e72e4',
      toast: true,
      position: 'top-end',
      timer: 5000,
      timerProgressBar: true,
      showConfirmButton: false
    });

    return { success: false, error: lastError };
  };

  const syncSavedTestIdFromSaveResult = (saveResult) => {
    const returnedTestId = saveResult?.testId;
    if (returnedTestId && returnedTestId !== savedTestIdRef.current) {
      setSavedTestId(returnedTestId);
    }
  };

  // Save test progress
  const handleSaveProgress = async (overrides = {}) => {
    // CRITICAL: Do not save if we don't have a valid savedTestId - this would create duplicate tests
    if (!testQuestions || testQuestions.length === 0) return { success: false };
    if (!savedTestId) {
      console.warn('handleSaveProgress called without savedTestId - skipping to prevent duplicate test creation');
      return { success: false };
    }

    // In review mode, only save annotations and marked questions (preserve completed status)
    if (isReviewMode) {
      if (savedTestId) {
        const reviewUpdateData = {
          testId: savedTestId,
          testName: currentTest?.testName || testName || generateAutoTestName(),
          status: 'completed', // Preserve completed status
          testQuestions,
          currentQuestionIndex: currentTest?.currentQuestionIndex || 0,
          selectedAnswers: currentTest?.selectedAnswers || selectedAnswers,
          markedQuestions, // Save updated marks
          submittedQuestions: currentTest?.submittedQuestions || submittedQuestions,
          annotations, // Save updated annotations (highlights, strikethroughs)
          timeSpent: currentTest?.timeSpent || timeSpentTrackingRef.current,
          timeRemaining: currentTest?.timeRemaining,
          isTimed: currentTest?.isTimed || isTimed,
          isTutor: currentTest?.isTutor !== undefined ? currentTest.isTutor : isTutor,
          selectionMode: currentTest?.selectionMode || selectionMode,
          timeAccommodation: currentTest?.timeAccommodation || timeAccommodation,
          questionTimeTracking: currentTest?.questionTimeTracking || questionTimeTracking,
          totalQuestions: testQuestions.length,
          subjects: currentTest?.subjects || [],
          units: currentTest?.units || [],
          score: currentTest?.score,
          correctAnswers: currentTest?.correctAnswers,
          createdAt: currentTest?.createdAt,
          completedAt: currentTest?.completedAt,
          updatedAt: Date.now()
        };
        const saveResult = await saveWithRetry(() => dispatch(saveTestProgress(reviewUpdateData)));
        syncSavedTestIdFromSaveResult(saveResult);
        return saveResult;
      }
      return { success: false };
    }

    // When resuming a test, use the saved subjects/units/topics from currentTest
    // Otherwise, generate from selected IDs or derive from test questions
    let subjectNames = [];
    let unitNames = [];
    let topicNames = [];

    if (currentTest?.subjects && currentTest.subjects.length > 0) {
      // Use saved subject/unit/topic names when resuming
      subjectNames = currentTest.subjects;
      unitNames = currentTest.units || [];
      topicNames = currentTest.topics || [];
    } else if (selectedSubjects.length > 0) {
      // Generate from selected IDs for standard mode (with code prefix)
      subjectNames = selectedSubjects
        .map(id => {
          const s = subjects?.find(s => s.subjectId === id);
          return s ? getDisplayName(s, activeSpecialty) : null;
        })
        .filter(Boolean);
      unitNames = selectedUnits
        .map(id => {
          const u = units?.find(u => u.unitId === id);
          return u ? getDisplayName(u, activeSpecialty) : null;
        })
        .filter(Boolean);
      topicNames = selectedTopics
        .map(id => {
          const t = topics?.find(t => t.topicId === id);
          return t ? getDisplayName(t, activeSpecialty) : null;
        })
        .filter(Boolean);
    } else {
      // Custom mode: derive subjects/units/topics from the actual test questions
      const subjectIdSet = new Set();
      const unitIdSet = new Set();
      const topicIdSet = new Set();
      testQuestions.forEach(q => {
        if (q.subjectId) subjectIdSet.add(q.subjectId);
        if (q.unitId) unitIdSet.add(q.unitId);
        if (q.topicId) topicIdSet.add(q.topicId);
      });
      subjectNames = [...subjectIdSet]
        .map(id => {
          const s = subjects?.find(s => s.subjectId === id);
          return s ? getDisplayName(s, activeSpecialty) : null;
        })
        .filter(Boolean);
      unitNames = [...unitIdSet]
        .map(id => {
          const u = units?.find(u => u.unitId === id);
          return u ? getDisplayName(u, activeSpecialty) : null;
        })
        .filter(Boolean);
      topicNames = [...topicIdSet]
        .map(id => {
          const t = topics?.find(t => t.topicId === id);
          return t ? getDisplayName(t, activeSpecialty) : null;
        })
        .filter(Boolean);
    }

    // Use overrides if provided, otherwise use state
    const submittedToSave = overrides.submittedQuestions !== undefined ? overrides.submittedQuestions : submittedQuestionsRef.current;
    const timeTrackingToSave = overrides.questionTimeTracking !== undefined ? overrides.questionTimeTracking : questionTimeTracking;
    const annotationsToSave = overrides.annotations !== undefined ? overrides.annotations : annotationsRef.current;

    const testData = {
      testId: savedTestId, // CRITICAL: Always use savedTestId - we guard against null above
      testName: testName || currentTest?.testName || generateAutoTestName(),
      status: 'in_progress',
      testQuestions,
      currentQuestionIndex: currentQuestionIndexRef.current, // Use ref to avoid stale closure issues
      selectedAnswers: selectedAnswersRef.current, // Use ref to avoid stale closure issues
      markedQuestions: markedQuestionsRef.current, // Use ref to avoid stale closure issues
      submittedQuestions: submittedToSave,
      annotations: annotationsToSave,
      timeSpent: timeSpentTrackingRef.current,
      timeRemaining: timeRemainingRef.current, // Use ref to avoid stale closure issues
      elapsedTime: !isTimed ? elapsedTimeRef.current : 0, // Save elapsed time for untimed tests
      isTimed,
      isTutor,
      selectionMode,
      timeAccommodation,
      questionTimeTracking: timeTrackingToSave,
      totalQuestions: testQuestions.length,
      subjects: subjectNames,
      units: unitNames,
      topics: topicNames,
      createdAt: currentTest?.createdAt || Date.now(),
      updatedAt: Date.now()
    };
    const saveResult = await saveWithRetry(() => dispatch(saveTestProgress(testData)));
    syncSavedTestIdFromSaveResult(saveResult);
    return saveResult;
  };

  // Save and exit test
  const handleSaveAndExit = () => {
    Swal.fire({
      title: 'Save & Pause?',
      text: 'Your progress will be saved and you can continue later.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#5e72e4',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, save and pause',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Saving Progress...',
          text: 'Please wait',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => { Swal.showLoading(); }
        });

        // Calculate updated time tracking for current question (only if not already submitted)
        let updatedTimeTracking = { ...questionTimeTracking };
        if (currentQuestionStartTime && testQuestions[currentQuestionIndex]) {
          const questionId = testQuestions[currentQuestionIndex].questionId;
          const isCurrentSubmitted = isTutor && submittedQuestions.includes(questionId);
          if (!isCurrentSubmitted) {
            const timeSpent = Math.floor((Date.now() - currentQuestionStartTime) / 1000);
            updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
          }
        }

        // Update state for UI consistency
        setQuestionTimeTracking(updatedTimeTracking);

        // Save current markup before exiting
        const updatedAnnotations = saveCurrentMarkup();

        // Save progress with the updated time tracking and annotations passed directly
        await handleSaveProgress({ questionTimeTracking: updatedTimeTracking, annotations: updatedAnnotations });

        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Your progress has been saved.',
          confirmButtonColor: '#5e72e4',
          timer: 1500,
          showConfirmButton: false
        });

        setTimeout(() => {
          setIsTestModalOpen(false);
          history.push('/client/question-bank/previous-tests');
        }, 1500);
      }
    });
  };

  // End test and show review modal
  const handleEndTest = () => {
    const answeredCount = Object.keys(selectedAnswers).length;

    if (answeredCount < testQuestions.length) {
      Swal.fire({
        title: 'Incomplete Test',
        text: `You have answered ${answeredCount} out of ${testQuestions.length} questions. Do you want to end the test anyway?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#5e72e4',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, end test',
        cancelButtonText: 'Continue test'
      }).then((result) => {
        if (result.isConfirmed) {
          showReportModal();
        }
      });
    } else {
      showReportModal();
    }
  };

  const showReportModal = () => {
    // Save time spent on current question before showing report
    if (currentQuestionStartTime && testQuestions[currentQuestionIndex]) {
      const timeSpent = Math.floor((Date.now() - currentQuestionStartTime) / 1000);
      const questionId = testQuestions[currentQuestionIndex].questionId;
      setQuestionTimeTracking(prev => ({
        ...prev,
        [questionId]: (prev[questionId] || 0) + timeSpent
      }));
      // Reset so we don't double count
      setCurrentQuestionStartTime(null);
    }

    // Save current markup before showing report
    saveCurrentMarkup();
    setIsReportModalOpen(true);
  };

  // Ref to hold the latest handleSubmitTest for use in timer callback (C1)
  const handleSubmitTestRef = useRef(null);

  const handleSubmitTest = async () => {
    // H1: double-click guard — prevent multiple submissions
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Stop auto-save to prevent it from overwriting status back to 'in_progress'
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }

    try {
    // Close report modal
    setIsReportModalOpen(false);

    Swal.fire({
      title: 'Submitting Test...',
      text: 'Calculating your results',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    // Calculate score
    let correctAnswers = 0;
    testQuestions.forEach(question => {
      const selectedAnswerId = selectedAnswers[question.questionId];
      const correctAnswer = question.answers?.find(a => a.isCorrect);

      if (selectedAnswerId === correctAnswer?.answerId) {
        correctAnswers++;
      }
    });

    const score = Math.round((correctAnswers / testQuestions.length) * 100);

    // When resuming a test, use the saved subjects/units/topics from currentTest
    // Otherwise, generate from selected IDs or derive from test questions
    let subjectNames = [];
    let unitNames = [];
    let topicNames = [];

    if (currentTest?.subjects && currentTest.subjects.length > 0) {
      // Use saved subject/unit/topic names when resuming
      subjectNames = currentTest.subjects;
      unitNames = currentTest.units || [];
      topicNames = currentTest.topics || [];
    } else if (selectedSubjects.length > 0) {
      // Generate from selected IDs for standard mode (with code prefix)
      subjectNames = selectedSubjects
        .map(id => {
          const s = subjects?.find(s => s.subjectId === id);
          return s ? getDisplayName(s, activeSpecialty) : null;
        })
        .filter(Boolean);
      unitNames = selectedUnits
        .map(id => {
          const u = units?.find(u => u.unitId === id);
          return u ? getDisplayName(u, activeSpecialty) : null;
        })
        .filter(Boolean);
      topicNames = selectedTopics
        .map(id => {
          const t = topics?.find(t => t.topicId === id);
          return t ? getDisplayName(t, activeSpecialty) : null;
        })
        .filter(Boolean);
    } else {
      // Custom mode: derive subjects/units/topics from the actual test questions
      const subjectIdSet = new Set();
      const unitIdSet = new Set();
      const topicIdSet = new Set();
      testQuestions.forEach(q => {
        if (q.subjectId) subjectIdSet.add(q.subjectId);
        if (q.unitId) unitIdSet.add(q.unitId);
        if (q.topicId) topicIdSet.add(q.topicId);
      });
      subjectNames = [...subjectIdSet]
        .map(id => {
          const s = subjects?.find(s => s.subjectId === id);
          return s ? getDisplayName(s, activeSpecialty) : null;
        })
        .filter(Boolean);
      unitNames = [...unitIdSet]
        .map(id => {
          const u = units?.find(u => u.unitId === id);
          return u ? getDisplayName(u, activeSpecialty) : null;
        })
        .filter(Boolean);
      topicNames = [...topicIdSet]
        .map(id => {
          const t = topics?.find(t => t.topicId === id);
          return t ? getDisplayName(t, activeSpecialty) : null;
        })
        .filter(Boolean);
    }

    // CRITICAL: Ensure we have a valid testId before submitting
    if (!savedTestId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Test ID not found. Please try again.',
        confirmButtonColor: '#5e72e4'
      });
      setIsSubmitting(false);
      return;
    }

    const testData = {
      testId: savedTestId, // CRITICAL: Always use savedTestId
      testName: testName || currentTest?.testName || generateAutoTestName(),
      status: 'completed',
      testQuestions,
      currentQuestionIndex,
      selectedAnswers,
      markedQuestions,
      submittedQuestions,
      annotations,
      timeSpent: timeSpentTrackingRef.current,
      timeRemaining,
      isTimed,
      isTutor,
      selectionMode,
      timeAccommodation,
      questionTimeTracking,
      totalQuestions: testQuestions.length,
      score,
      correctAnswers,
      subjects: subjectNames,
      units: unitNames,
      topics: topicNames,
      createdAt: currentTest?.createdAt || Date.now(),
      updatedAt: Date.now(),
      completedAt: Date.now()
    };
    const saveResult = await dispatch(saveTestProgress(testData));
    syncSavedTestIdFromSaveResult(saveResult);

    // Update question analytics
    // Only include time data for Timed + Untutored mode tests
    // Skip omitted questions so timesAnswered reflects actual attempts, not just appearances
    const shouldTrackTime = isTimed && !isTutor;
    for (const question of testQuestions) {
      const selectedAnswerId = selectedAnswers[question.questionId];
      const wasOmitted = !selectedAnswerId;

      // Only update analytics for questions the user actually answered
      if (wasOmitted) continue;

      const correctAnswer = question.answers?.find(a => a.isCorrect);
      const isCorrect = selectedAnswerId === correctAnswer?.answerId;

      try {
        const questionRef = doc(db, getCollectionNameForSpecialty('questionBankQuestions', activeSpecialty), question.questionId);
        const questionDoc = await getDoc(questionRef);

        if (questionDoc.exists()) {
          const currentData = questionDoc.data();
          const newTimesAnswered = (currentData.timesAnswered || 0) + 1;
          const newTimesCorrect = (currentData.timesCorrect || 0) + (isCorrect ? 1 : 0);

          // Track answer distribution - how many times each answer was chosen
          const answerDistribution = currentData.answerDistribution || {};
          answerDistribution[selectedAnswerId] = (answerDistribution[selectedAnswerId] || 0) + 1;

          const updateData = {
            timesAnswered: newTimesAnswered,
            timesCorrect: newTimesCorrect,
            answerDistribution: answerDistribution,
            lastUsedAt: serverTimestamp()
          };

          if (shouldTrackTime) {
            // Use actual per-question time from tracking instead of global average
            const actualTimeForQuestion = questionTimeTracking[question.questionId] || 0;
            const currentAvgTime = currentData.averageTimeSpent || 0;
            const timesAnsweredForTime = currentData.timesAnsweredForTime || 0;
            const newTimesAnsweredForTime = timesAnsweredForTime + 1;
            const newAvgTime = Math.round(((currentAvgTime * timesAnsweredForTime) + actualTimeForQuestion) / newTimesAnsweredForTime);

            updateData.averageTimeSpent = newAvgTime;
            updateData.timesAnsweredForTime = newTimesAnsweredForTime;
          }

          await updateDoc(questionRef, updateData);
        }
      } catch (error) {
        console.error('Error updating question analytics:', error);
      }
    }

    Swal.fire({
      title: 'Test Completed!',
      html: `
        <div style="text-align: center;">
          <h2 style="color: ${score >= 70 ? '#2dce89' : score >= 50 ? '#fb6340' : '#f5365c'}; font-size: 3rem; margin: 20px 0;">
            ${score}%
          </h2>
          <p style="font-size: 1.2rem; margin: 10px 0;">
            <strong>${correctAnswers}</strong> out of <strong>${testQuestions.length}</strong> correct
          </p>
          <p style="color: #8898aa; margin-top: 15px;">
            Time spent: ${Math.floor(timeSpentTrackingRef.current / 60)}m ${timeSpentTrackingRef.current % 60}s
          </p>
        </div>
      `,
      icon: score >= 70 ? 'success' : score >= 50 ? 'info' : 'error',
      confirmButtonColor: '#5e72e4',
      confirmButtonText: 'View Results'
    }).then(() => {
      setIsTestModalOpen(false);
      history.push('/client/question-bank/previous-tests', { showAnalysis: savedTestId });
    });
    } catch (error) {
      console.error('Error submitting test:', error);
      setIsSubmitting(false);
      Swal.fire('Error', 'Failed to submit test. Please try again.', 'error');
    }
  };

  // Keep ref in sync so timer callback always calls the latest version (C1)
  handleSubmitTestRef.current = handleSubmitTest;

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
  };

  const handleReviewQuestion = (index) => {
    setIsReportModalOpen(false);
    handleGoToQuestion(index);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortedQuestions = () => {
    if (!sortBy) return testQuestions;

    const questionsWithIndex = testQuestions.map((q, index) => ({ question: q, originalIndex: index }));

    return [...questionsWithIndex].sort((a, b) => {
      if (sortBy === 'status') {
        const aAnswered = selectedAnswers[a.question.questionId] !== undefined;
        const bAnswered = selectedAnswers[b.question.questionId] !== undefined;

        if (isTutor && aAnswered && bAnswered) {
          // Both answered, sort by correct/incorrect
          const aCorrect = selectedAnswers[a.question.questionId] === a.question.answers?.find(ans => ans.isCorrect)?.answerId;
          const bCorrect = selectedAnswers[b.question.questionId] === b.question.answers?.find(ans => ans.isCorrect)?.answerId;

          if (aCorrect === bCorrect) return 0;
          if (sortOrder === 'asc') {
            return aCorrect ? -1 : 1; // Correct first
          } else {
            return aCorrect ? 1 : -1; // Incorrect first
          }
        }

        // Sort by answered/unanswered
        if (aAnswered === bAnswered) return 0;
        if (sortOrder === 'asc') {
          return aAnswered ? 1 : -1; // Unanswered first
        } else {
          return aAnswered ? -1 : 1; // Answered first
        }
      } else if (sortBy === 'flagged') {
        const aMarked = markedQuestions.includes(a.question.questionId);
        const bMarked = markedQuestions.includes(b.question.questionId);

        if (aMarked === bMarked) return 0;
        if (sortOrder === 'asc') {
          return aMarked ? -1 : 1; // Flagged first
        } else {
          return aMarked ? 1 : -1; // Unflagged first
        }
      }
      return 0;
    });
  };

  // Generate automatic test name based on date/time and selected topics
  const generateAutoTestName = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    // Get topic names (prioritize topics, then units, then subjects)
    let topicLabels = [];

    if (selectedTopics.length > 0) {
      topicLabels = selectedTopics
        .slice(0, 3) // Limit to 3 topics
        .map(id => {
          const t = topics?.find(t => t.topicId === id);
          return t ? t.name : null;
        })
        .filter(Boolean);
    } else if (selectedUnits.length > 0) {
      topicLabels = selectedUnits
        .slice(0, 3)
        .map(id => {
          const u = units?.find(u => u.unitId === id);
          return u ? u.name : null;
        })
        .filter(Boolean);
    } else if (selectedSubjects.length > 0) {
      topicLabels = selectedSubjects
        .slice(0, 3)
        .map(id => {
          const s = subjects?.find(s => s.subjectId === id);
          return s ? s.name : null;
        })
        .filter(Boolean);
    }

    const topicStr = topicLabels.length > 0
      ? ` - ${topicLabels.join(', ')}${(selectedTopics.length > 3 || selectedUnits.length > 3 || selectedSubjects.length > 3) ? '...' : ''}`
      : '';

    return `${dateStr} ${timeStr}${topicStr}`;
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    Swal.fire({
      title: 'Generating Test...',
      text: 'Preparing your questions',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    // Refetch questions to get latest analytics
    await dispatch(fetchQuestions());

    // Wait a moment for Redux state to update
    await new Promise(resolve => setTimeout(resolve, 100));

    let selectedQuestions = [];

    if (selectionMode === 'custom') {
      // Custom mode: use question codes
      const questionCodeArray = customQuestionIds.replace(/\s/g, '').split(',').filter(id => id.trim());

      // Find questions by questionCode and group by passage
      const passageGroups = {};
      const standaloneQuestions = [];
      const notFoundIds = [];

      questionCodeArray.forEach(qCode => {
        const question = questions.find(q => q.questionCode === qCode || q.questionId === qCode);
        if (question) {
          if (question.passageId) {
            if (!passageGroups[question.passageId]) {
              passageGroups[question.passageId] = {
                passage: passages.find(p => p.passageId === question.passageId),
                questions: []
              };
            }
            passageGroups[question.passageId].questions.push(question);
          } else {
            standaloneQuestions.push(question);
          }
        } else {
          notFoundIds.push(qCode);
        }
      });

      if (notFoundIds.length > 0) {
        Swal.fire('Warning', `The following question IDs were not found or are deactivated: ${notFoundIds.join(', ')}`, 'warning');
      }

      // Build selected questions array with passage metadata
      Object.values(passageGroups).forEach(group => {
        group.questions.forEach((question, index) => {
          selectedQuestions.push({
            ...question,
            answers: question.choices?.map(choice => ({
              answerId: choice.choiceId,
              answerText: choice.text,
              answerHtml: typeof choice.text === 'string' ? choice.text : draftRawToHtml(choice.text),
              isCorrect: question.correctAnswer?.includes(choice.choiceId)
            })) || [],
            explanation: question.explanationText,
            passageData: group.passage,
            isFirstInPassage: index === 0,
            passageQuestionNumber: index + 1,
            totalPassageQuestions: group.questions.length
          });
        });
      });

      standaloneQuestions.forEach(question => {
        selectedQuestions.push({
          ...question,
          answers: question.choices?.map(choice => ({
            answerId: choice.choiceId,
            answerText: choice.text,
            answerHtml: typeof choice.text === 'string' ? choice.text : draftRawToHtml(choice.text),
            isCorrect: question.correctAnswer?.includes(choice.choiceId)
          })) || [],
          explanation: question.explanationText
        });
      });

      if (selectedQuestions.length === 0) {
        Swal.fire('Error', 'No valid questions found with the provided IDs', 'error');
        return;
      }
    } else {
      // Standard mode: select questions based on actual count (1:1)
      // Passages must be included in full, discretes fill in gaps

      // Separate passages and standalone questions, shuffle for randomization
      const passageUnits = questionUnits.filter(u => u.type === 'passage').sort(() => Math.random() - 0.5);
      const standaloneUnits = questionUnits.filter(u => u.type === 'standalone').sort(() => Math.random() - 0.5);

      let currentCount = 0;
      const selectedUnitsForTest = [];

      // First, add passages until we reach or exceed the target
      for (const passageUnit of passageUnits) {
        const passageQuestionCount = passageUnit.questions.length;

        // If adding this passage would get us closer to the target, add it
        // Also add if we haven't reached the target yet
        if (currentCount < numberOfQuestions) {
          selectedUnitsForTest.push(passageUnit);
          currentCount += passageQuestionCount;
        }

        // If we've reached or exceeded the target, stop adding passages
        if (currentCount >= numberOfQuestions) {
          break;
        }
      }

      // If we're still under the target, fill with discrete questions
      if (currentCount < numberOfQuestions) {
        const remainingNeeded = numberOfQuestions - currentCount;
        const discretesToAdd = standaloneUnits.slice(0, remainingNeeded);
        selectedUnitsForTest.push(...discretesToAdd);
        currentCount += discretesToAdd.length;
      }

      // If we still need more and have unused passages, add them
      if (currentCount < numberOfQuestions) {
        for (const passageUnit of passageUnits) {
          if (!selectedUnitsForTest.includes(passageUnit)) {
            selectedUnitsForTest.push(passageUnit);
            currentCount += passageUnit.questions.length;
            if (currentCount >= numberOfQuestions) {
              break;
            }
          }
        }
      }

      // Convert selected units to flat question array with passage metadata
      selectedUnitsForTest.forEach(unit => {
        if (unit.type === 'passage') {
          // Include ALL questions from this passage
          unit.questions.forEach((question, index) => {
            selectedQuestions.push({
              ...question,
              answers: question.choices?.map(choice => ({
                answerId: choice.choiceId,
                answerText: choice.text,
                answerHtml: typeof choice.text === 'string' ? choice.text : draftRawToHtml(choice.text),
                isCorrect: question.correctAnswer?.includes(choice.choiceId)
              })) || [],
              explanation: question.explanationText,
              passageData: unit.passage,
              isFirstInPassage: index === 0,
              passageQuestionNumber: index + 1,
              totalPassageQuestions: unit.questions.length
            });
          });
        } else {
          selectedQuestions.push({
            ...unit.question,
            answers: unit.question.choices?.map(choice => ({
              answerId: choice.choiceId,
              answerText: choice.text,
              answerHtml: typeof choice.text === 'string' ? choice.text : draftRawToHtml(choice.text),
              isCorrect: unit.question.correctAnswer?.includes(choice.choiceId)
            })) || [],
            explanation: unit.question.explanationText
          });
        }
      });
    }

    // Generate subject, unit, and topic names (with code prefix) BEFORE opening modal
    let subjectNames = [];
    let unitNames = [];
    let topicNames = [];

    if (selectedSubjects.length > 0) {
      // Standard mode: use selected subjects/units/topics
      subjectNames = selectedSubjects
        .map(id => {
          const s = subjects?.find(s => s.subjectId === id);
          return s ? getDisplayName(s, activeSpecialty) : null;
        })
        .filter(Boolean);
      unitNames = selectedUnits
        .map(id => {
          const u = units?.find(u => u.unitId === id);
          return u ? getDisplayName(u, activeSpecialty) : null;
        })
        .filter(Boolean);
      topicNames = selectedTopics
        .map(id => {
          const t = topics?.find(t => t.topicId === id);
          return t ? getDisplayName(t, activeSpecialty) : null;
        })
        .filter(Boolean);
    } else {
      // Custom mode: derive from test questions
      const subjectIdSet = new Set();
      const unitIdSet = new Set();
      const topicIdSet = new Set();
      selectedQuestions.forEach(q => {
        if (q.subjectId) subjectIdSet.add(q.subjectId);
        if (q.unitId) unitIdSet.add(q.unitId);
        if (q.topicId) topicIdSet.add(q.topicId);
      });
      subjectNames = [...subjectIdSet]
        .map(id => {
          const s = subjects?.find(s => s.subjectId === id);
          return s ? getDisplayName(s, activeSpecialty) : null;
        })
        .filter(Boolean);
      unitNames = [...unitIdSet]
        .map(id => {
          const u = units?.find(u => u.unitId === id);
          return u ? getDisplayName(u, activeSpecialty) : null;
        })
        .filter(Boolean);
      topicNames = [...topicIdSet]
        .map(id => {
          const t = topics?.find(t => t.topicId === id);
          return t ? getDisplayName(t, activeSpecialty) : null;
        })
        .filter(Boolean);
    }

    // CRITICAL FIX: Save test to database FIRST and get the testId BEFORE opening modal
    // This prevents race conditions where auto-save fires before we have a valid testId
    const autoGeneratedName = testName || generateAutoTestName();
    const initialTestData = {
      testId: `test_${Date.now()}`,
      testName: autoGeneratedName,
      status: 'in_progress',
      testQuestions: selectedQuestions,
      currentQuestionIndex: 0,
      selectedAnswers: {},
      markedQuestions: [],
      submittedQuestions: [],
      annotations: {},
      timeSpent: 0,
      timeRemaining: isTimed ? Math.round(selectedQuestions.length * 66 * timeAccommodation) : null,
      elapsedTime: 0,
      isTimed,
      isTutor,
      selectionMode,
      timeAccommodation,
      questionTimeTracking: {},
      totalQuestions: selectedQuestions.length,
      subjects: subjectNames,
      units: unitNames,
      topics: topicNames,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    // Save to database and wait for the testId before proceeding
    const result = await dispatch(saveTestProgress(initialTestData));

    if (!result?.testId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create test. Please try again.',
        confirmButtonColor: '#5e72e4'
      });
      return;
    }

    // Now set all state with the confirmed testId
    const confirmedTestId = result.testId;

    setTestQuestions(selectedQuestions);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setMarkedQuestions([]);
    setSubmittedQuestions([]);
    setAnnotations({});
    setShowExplanation(false);
    setTestStartTime(new Date());
    setSavedTestId(confirmedTestId); // Set the confirmed testId BEFORE opening modal
    loadInProgressQuestionIds(); // Refresh exclusion list for next test creation
    // Keep testName — it was entered in the form before creating the test
    setTimeSpentTracking(0);
    timeSpentTrackingRef.current = 0;
    setQuestionTimeTracking({});
    setCurrentQuestionStartTime(Date.now());
    setIsReviewMode(false);
    setTestLoaded(true);

    if (isTimed) {
      const baseTimePerQuestion = 66;
      const totalTime = Math.round(selectedQuestions.length * baseTimePerQuestion * timeAccommodation);
      setTimeRemaining(totalTime);
    } else {
      setTimeRemaining(null);
    }
    // Reset elapsed time for fresh test
    setElapsedTime(0);

    Swal.close();
    setIsTestModalOpen(true);
  };

  const handleCloseTest = async () => {
    if (isReviewMode) {
      // Save annotations and marks before closing in review mode
      await handleSaveProgress();
      setIsTestModalOpen(false);
      history.push('/client/question-bank/previous-tests');
      return;
    }

    Swal.fire({
      title: 'Pause Test?',
      text: 'Your progress will be saved automatically.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Save & Pause',
      cancelButtonText: 'Continue Test',
      confirmButtonColor: '#5e72e4',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed) {
        handleSaveAndExit();
      }
    });
  };

  // Debounce ref for answer selection saves
  const answerSaveTimeoutRef = useRef(null);

  const handleSelectAnswer = (questionId, answerId) => {
    // Toggle: click again to deselect
    const newValue = selectedAnswersRef.current[questionId] === answerId ? null : answerId;

    // Update the ref immediately for reliable saves
    const updatedAnswers = {
      ...selectedAnswersRef.current,
      [questionId]: newValue
    };
    selectedAnswersRef.current = updatedAnswers;

    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: newValue
    }));

    // Debounced save - wait 1 second after last selection before saving
    // This prevents excessive saves when user is quickly changing their answer
    if (savedTestId && !isReviewMode) {
      if (answerSaveTimeoutRef.current) {
        clearTimeout(answerSaveTimeoutRef.current);
      }
      answerSaveTimeoutRef.current = setTimeout(() => {
        handleSaveProgress();
      }, 1000);
    }
  };

  const handleSubmitAnswer = () => {
    if (isTutor && selectedAnswers[currentQuestion.questionId]) {
      // Add to submitted questions and get updated list
      const questionId = currentQuestion.questionId;

      // Calculate updated time tracking
      let updatedTimeTracking = { ...questionTimeTracking };
      if (currentQuestionStartTime) {
        const timeSpent = Math.floor((Date.now() - currentQuestionStartTime) / 1000);
        updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
        setQuestionTimeTracking(updatedTimeTracking);
        // Stop tracking time for this question after submission
        setCurrentQuestionStartTime(null);
      }

      // Calculate updated submitted questions
      const updatedSubmittedQuestions = submittedQuestions.includes(questionId)
        ? submittedQuestions
        : [...submittedQuestions, questionId];
      setSubmittedQuestions(updatedSubmittedQuestions);

      // Pause the main timer while reviewing explanation in tutor mode
      setIsPaused(true);

      // Update local question analytics so "chose this" and "Correct %" reflect this answer immediately
      const selectedAnswerId = selectedAnswers[questionId];
      const correctAnswer = currentQuestion.answers?.find(a => a.isCorrect);
      const isCorrect = selectedAnswerId === correctAnswer?.answerId;
      setTestQuestions(prev => prev.map(q => {
        if (q.questionId !== questionId) return q;
        const dist = { ...(q.answerDistribution || {}) };
        dist[selectedAnswerId] = (dist[selectedAnswerId] || 0) + 1;
        return {
          ...q,
          timesCorrect: (q.timesCorrect || 0) + (isCorrect ? 1 : 0),
          timesAnswered: (q.timesAnswered || 0) + 1,
          answerDistribution: dist,
        };
      }));

      // Save current markup before showing explanation
      saveCurrentMarkup();
      setShowExplanation(true);

      // Save progress with the updated values passed directly
      setTimeout(() => {
        handleSaveProgress({
          submittedQuestions: updatedSubmittedQuestions,
          questionTimeTracking: updatedTimeTracking
        });
      }, 500);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < testQuestions.length - 1) {
      // Save time spent on current question (only if not already submitted)
      let updatedTimeTracking = { ...questionTimeTrackingRef.current };
      if (currentQuestionStartTime && testQuestions[currentQuestionIndex]) {
        const questionId = testQuestions[currentQuestionIndex].questionId;
        const isCurrentSubmitted = isTutor && submittedQuestions.includes(questionId);
        if (!isCurrentSubmitted) {
          const timeSpent = Math.floor((Date.now() - currentQuestionStartTime) / 1000);
          updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
          setQuestionTimeTracking(updatedTimeTracking);
        }
      }

      // Save current markup before moving to next question
      saveCurrentMarkup();
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setShowExplanation(false);

      // Only start tracking time if the question is not already submitted (tutor mode)
      const nextQuestionId = testQuestions[nextIndex]?.questionId;
      const isNextSubmitted = isTutor && submittedQuestions.includes(nextQuestionId);
      setCurrentQuestionStartTime(isNextSubmitted ? null : Date.now());

      // Pause/resume the main timer based on whether the target question is submitted
      if (isTutor && isNextSubmitted && !isPaused) {
        setIsPaused(true);
      } else if (isTutor && !isNextSubmitted && isPaused) {
        setIsPaused(false);
      }

      // Auto-save progress on navigation (prevents answer loss)
      if (savedTestId && !isReviewMode) {
        handleSaveProgress({ questionTimeTracking: updatedTimeTracking });
      }

    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      // Save time spent on current question (only if not already submitted)
      let updatedTimeTracking = { ...questionTimeTrackingRef.current };
      if (currentQuestionStartTime && testQuestions[currentQuestionIndex]) {
        const questionId = testQuestions[currentQuestionIndex].questionId;
        const isCurrentSubmitted = isTutor && submittedQuestions.includes(questionId);
        if (!isCurrentSubmitted) {
          const timeSpent = Math.floor((Date.now() - currentQuestionStartTime) / 1000);
          updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
          setQuestionTimeTracking(updatedTimeTracking);
        }
      }

      // Save current markup before moving to previous question
      saveCurrentMarkup();
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      setShowExplanation(false);

      // Only start tracking time if the question is not already submitted (tutor mode)
      const prevQuestionId = testQuestions[prevIndex]?.questionId;
      const isPrevSubmitted = isTutor && submittedQuestions.includes(prevQuestionId);
      setCurrentQuestionStartTime(isPrevSubmitted ? null : Date.now());

      // Pause/resume the main timer based on whether the target question is submitted
      if (isTutor && isPrevSubmitted && !isPaused) {
        setIsPaused(true);
      } else if (isTutor && !isPrevSubmitted && isPaused) {
        setIsPaused(false);
      }

      // Auto-save progress on navigation (prevents answer loss)
      if (savedTestId && !isReviewMode) {
        handleSaveProgress({ questionTimeTracking: updatedTimeTracking });
      }

    }
  };

  const handleMarkQuestion = () => {
    const currentQuestionId = testQuestions[currentQuestionIndex]?.questionId;
    if (currentQuestionId) {
      setMarkedQuestions(prev => {
        if (prev.includes(currentQuestionId)) {
          return prev.filter(id => id !== currentQuestionId);
        }
        return [...prev, currentQuestionId];
      });
      // Auto-save after marking/unmarking question
      if (savedTestId && !isReviewMode) {
        if (answerSaveTimeoutRef.current) {
          clearTimeout(answerSaveTimeoutRef.current);
        }
        answerSaveTimeoutRef.current = setTimeout(() => {
          handleSaveProgress();
        }, 1000);
      }
    }
  };

  const handleGoToQuestion = (index) => {
    // Save time spent on current question (only if not already submitted)
    let updatedTimeTracking = { ...questionTimeTrackingRef.current };
    if (currentQuestionStartTime && testQuestions[currentQuestionIndex]) {
      const questionId = testQuestions[currentQuestionIndex].questionId;
      const isCurrentSubmitted = isTutor && submittedQuestions.includes(questionId);
      if (!isCurrentSubmitted) {
        const timeSpent = Math.floor((Date.now() - currentQuestionStartTime) / 1000);
        updatedTimeTracking[questionId] = (updatedTimeTracking[questionId] || 0) + timeSpent;
        setQuestionTimeTracking(updatedTimeTracking);
      }
    }

    // Save current markup before changing question
    saveCurrentMarkup();
    setCurrentQuestionIndex(index);
    setShowExplanation(false);

    // Only start tracking time if the question is not already submitted (tutor mode)
    const targetQuestionId = testQuestions[index]?.questionId;
    const isTargetSubmitted = isTutor && submittedQuestions.includes(targetQuestionId);
    setCurrentQuestionStartTime(isTargetSubmitted ? null : Date.now());

    // Pause/resume the main timer based on whether the target question is submitted
    if (isTutor && isTargetSubmitted && !isPaused) {
      setIsPaused(true);
    } else if (isTutor && !isTargetSubmitted && isPaused) {
      setIsPaused(false);
    }

    // Auto-save progress on navigation (prevents answer loss)
    if (savedTestId && !isReviewMode) {
      handleSaveProgress({ questionTimeTracking: updatedTimeTracking });
    }
  };

  useEffect(() => {
    if (!isTimed || !isTestModalOpen || isReviewMode) return;
    autoSubmitTriggeredRef.current = false;
    const timer = setInterval(() => {
      if (isPausedRef.current) return;

      setTimeRemaining(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          return prev === null ? null : 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimed, isTestModalOpen, isReviewMode]);

  // Auto-submit when timed test reaches zero
  useEffect(() => {
    if (!isTimed || timeRemaining !== 0 || isReviewMode || !isTestModalOpen) return;
    if (autoSubmitTriggeredRef.current) return;
    autoSubmitTriggeredRef.current = true;

    Swal.fire({
      icon: 'info',
      title: 'Time\'s Up!',
      text: 'Your test time has ended. Submitting your test now.',
      confirmButtonColor: '#5e72e4',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then(() => {
      if (handleSubmitTestRef.current) {
        handleSubmitTestRef.current();
      }
    });
  }, [timeRemaining, isTimed, isReviewMode, isTestModalOpen]);

  // Elapsed time counter for untimed tests - counts up
  useEffect(() => {
    if (isTimed || !isTestModalOpen || isReviewMode) return;

    // Don't reset - continue from saved elapsedTime (loaded from currentTest)
    const timer = setInterval(() => {
      // Don't increment if paused (user is idle)
      if (isPausedRef.current) return;

      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimed, isTestModalOpen, isReviewMode]);

  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Save current markup to annotations
  const saveCurrentMarkup = () => {
    const currentQ = testQuestions[currentQuestionIndex];
    if (!currentQ) return annotationsRef.current;

    const passageElement = document.getElementById('passage-content');
    const questionElement = document.getElementById('question-content');

    const updatedAnnotations = { ...annotationsRef.current };
    if (!updatedAnnotations[currentQ.questionId]) {
      updatedAnnotations[currentQ.questionId] = {};
    }

    // Save question-specific markup
    if (questionElement) {
      updatedAnnotations[currentQ.questionId].questionMarkup = questionElement.innerHTML;
    }

    // Save answer choices markup (question-specific)
    if (currentQ.answers) {
      const answersMarkup = {};
      currentQ.answers.forEach(answer => {
        const answerElement = document.getElementById(`answer-${answer.answerId}`);
        if (answerElement) {
          answersMarkup[answer.answerId] = answerElement.innerHTML;
        }
      });
      updatedAnnotations[currentQ.questionId].answersMarkup = answersMarkup;
    }

    // Save passage markup - share across all questions in the same passage
    if (passageElement && currentQ.passageId) {
      const passageMarkup = passageElement.innerHTML;
      // Find all questions that share this passage and update their passage markup
      testQuestions.forEach(q => {
        if (q.passageId === currentQ.passageId) {
          if (!updatedAnnotations[q.questionId]) {
            updatedAnnotations[q.questionId] = {};
          }
          updatedAnnotations[q.questionId].passageMarkup = passageMarkup;
        }
      });
    } else if (passageElement) {
      // Standalone question with passage content
      updatedAnnotations[currentQ.questionId].passageMarkup = passageElement.innerHTML;
    }

    setAnnotations(updatedAnnotations);
    return updatedAnnotations;
  };

  // Helper function to get all text nodes in a range
  const getTextNodesInRange = (range) => {
    const textNodes = [];
    const startNode = range.startContainer;
    const endNode = range.endContainer;

    // If the selection is within a single text node
    if (startNode === endNode && startNode.nodeType === Node.TEXT_NODE) {
      return [startNode];
    }

    // Walk through all nodes in the range
    const walkNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Check if this text node is within our range
        const nodeRange = document.createRange();
        nodeRange.selectNodeContents(node);

        // Check if ranges intersect
        if (range.compareBoundaryPoints(Range.END_TO_START, nodeRange) <= 0 &&
            range.compareBoundaryPoints(Range.START_TO_END, nodeRange) >= 0) {
          textNodes.push(node);
        }
      } else {
        // Recursively walk child nodes
        for (let i = 0; i < node.childNodes.length; i++) {
          walkNode(node.childNodes[i]);
        }
      }
    };

    // Start walking from the common ancestor
    const commonAncestor = range.commonAncestorContainer;
    if (commonAncestor.nodeType === Node.TEXT_NODE) {
      textNodes.push(commonAncestor);
    } else {
      walkNode(commonAncestor);
    }

    return textNodes;
  };

  // Check whether a DOM node is inside one of the allowed markup containers
  // (passage, question text, answer choices, explanation).
  const isInsideMarkupArea = (node) => {
    const allowedIds = ['passage-content', 'passage-content-mobile', 'question-content', 'explanation-content', 'explanation-content-tutor'];
    let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (el) {
      if (el.id && (allowedIds.includes(el.id) || el.id.startsWith('answer-'))) return true;
      if (el.classList && el.classList.contains('answer-options')) return true;
      el = el.parentElement;
    }
    return false;
  };

  // Text selection and markup handlers
  const handleTextSelection = (mode) => {
    if (!window.getSelection) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0 || selection.toString().trim() === '') return;

    try {
      const range = selection.getRangeAt(0);

      // Only allow markup inside passage, question, choices, or explanation areas
      if (!isInsideMarkupArea(range.startContainer) || !isInsideMarkupArea(range.endContainer)) {
        selection.removeAllRanges();
        return;
      }

      // Get all text nodes in the selection
      const textNodes = getTextNodesInRange(range);

      if (textNodes.length === 0) {
        throw new Error('No text nodes found in selection');
      }

      // Process nodes in reverse order to avoid DOM position issues
      for (let i = textNodes.length - 1; i >= 0; i--) {
        const textNode = textNodes[i];

        // Determine the start and end offsets for this text node
        let startOffset = 0;
        let endOffset = textNode.textContent.length;

        if (textNode === range.startContainer) {
          startOffset = range.startOffset;
        }
        if (textNode === range.endContainer) {
          endOffset = range.endOffset;
        }

        // Skip if no content to highlight
        if (startOffset >= endOffset) continue;

        // Create a range for this portion of the text node
        const nodeRange = document.createRange();
        nodeRange.setStart(textNode, startOffset);
        nodeRange.setEnd(textNode, endOffset);

        // Create the span element
        const span = document.createElement('span');
        if (mode === 'highlight') {
          span.style.backgroundColor = highlightColor;
          span.className = 'user-highlight';
          span.setAttribute('data-highlight-color', highlightColor);
        } else if (mode === 'strikethrough') {
          span.style.textDecoration = 'line-through';
          span.style.textDecorationColor = '#ff0000';
          span.style.textDecorationThickness = '2px';
          span.className = 'user-strikethrough';
        }

        // Try to wrap the content
        try {
          // For partial text nodes, we need to split the text node first
          if (startOffset > 0 || endOffset < textNode.textContent.length) {
            // Extract the text to be highlighted
            const beforeText = textNode.textContent.substring(0, startOffset);
            const highlightText = textNode.textContent.substring(startOffset, endOffset);
            const afterText = textNode.textContent.substring(endOffset);

            // Create text nodes
            span.textContent = highlightText;

            // Replace the original text node with our new structure
            const parent = textNode.parentNode;
            const beforeNode = document.createTextNode(beforeText);
            const afterNode = document.createTextNode(afterText);

            // Insert in order: before, span, after
            parent.insertBefore(afterNode, textNode.nextSibling);
            parent.insertBefore(span, afterNode);
            parent.insertBefore(beforeNode, span);
            parent.removeChild(textNode);
          } else {
            // Wrap the entire text node
            nodeRange.surroundContents(span);
          }
        } catch (e) {
          console.error('Failed to wrap text node:', e, textNode);
        }
      }

      selection.removeAllRanges();

      // Save markup after applying, then auto-save to backend
      setTimeout(() => {
        saveCurrentMarkup();
        // Debounced save to backend after markup change (reuse existing timeout ref)
        if (savedTestId && !isReviewMode) {
          if (answerSaveTimeoutRef.current) {
            clearTimeout(answerSaveTimeoutRef.current);
          }
          answerSaveTimeoutRef.current = setTimeout(() => {
            handleSaveProgress();
          }, 1500);
        }
      }, 100);
    } catch (error) {
      console.error('Failed to apply markup:', error);
      alert('Unable to apply markup to this selection. Please try again.');
    }
  };

  const clearAllMarkup = () => {
    const highlights = document.querySelectorAll('.user-highlight, .user-strikethrough');

    if (highlights.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Markup',
        text: 'There are no highlights or strikethroughs to clear.',
        confirmButtonColor: '#5e72e4'
      });
      return;
    }

    Swal.fire({
      title: 'Clear All Markup?',
      text: `This will remove all ${highlights.length} highlight(s) and strikethrough(s) from this question.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, clear all',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        highlights.forEach(el => {
          const parent = el.parentNode;
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
          parent.normalize();
        });

        // Save cleared markup, then auto-save to backend
        setTimeout(() => {
          saveCurrentMarkup();
          if (savedTestId && !isReviewMode) {
            if (answerSaveTimeoutRef.current) {
              clearTimeout(answerSaveTimeoutRef.current);
            }
            answerSaveTimeoutRef.current = setTimeout(() => {
              handleSaveProgress();
            }, 1500);
          }
        }, 100);
      }
    });
  };

  // Restore markup and explanation state when changing questions
  useEffect(() => {
    const currentQ = testQuestions[currentQuestionIndex];
    if (!currentQ) return;

    // Check if this question was previously submitted (only when question index changes)
    const wasSubmitted = submittedQuestions.includes(currentQ.questionId);
    if (wasSubmitted && isTutor) {
      setShowExplanation(true);
    }

    const savedAnnotation = annotations[currentQ.questionId];

    // For passage questions, try to find passage markup from any sibling question if current doesn't have it
    let passageMarkupToRestore = savedAnnotation?.passageMarkup;
    if (!passageMarkupToRestore && currentQ.passageId) {
      // Look for passage markup from any question sharing the same passage
      for (const q of testQuestions) {
        if (q.passageId === currentQ.passageId && annotations[q.questionId]?.passageMarkup) {
          passageMarkupToRestore = annotations[q.questionId].passageMarkup;
          break;
        }
      }
    }

    if (!savedAnnotation && !passageMarkupToRestore) return;

    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      const passageElement = document.getElementById('passage-content');
      const questionElement = document.getElementById('question-content');

      // Restore passage markup (shared across passage questions)
      if (passageElement && passageMarkupToRestore) {
        passageElement.innerHTML = passageMarkupToRestore;
      }

      // Restore question-specific markup
      if (questionElement && savedAnnotation?.questionMarkup) {
        questionElement.innerHTML = savedAnnotation.questionMarkup;
      }

      // Restore answer choices markup
      if (savedAnnotation?.answersMarkup && currentQ.answers) {
        currentQ.answers.forEach(answer => {
          const answerElement = document.getElementById(`answer-${answer.answerId}`);
          if (answerElement && savedAnnotation.answersMarkup[answer.answerId]) {
            answerElement.innerHTML = savedAnnotation.answersMarkup[answer.answerId];
          }
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [currentQuestionIndex, testQuestions, annotations]);

  // M10/M9: Use shared sanitized helper for all Draft.js rendering
  const renderPassagePreview = (passageText) => draftRawToHtml(passageText);

  const renderQuestionText = (questionText) => {
    if (typeof questionText === 'string') return questionText;
    return draftRawToHtml(questionText);
  };

  const renderChoiceText = (choiceText) => {
    if (typeof choiceText === 'string') return choiceText;
    return draftRawToHtml(choiceText);
  };

  const renderExplanationText = (explanationText) => {
    if (typeof explanationText === 'string') return explanationText;
    return draftRawToHtml(explanationText);
  };

  const currentQuestion = testQuestions[currentQuestionIndex];

  return (
    <>
    <style>{`
      .subject-masonry-grid { column-count: 2; column-gap: 1rem; }
      @media (max-width: 767px) { .subject-masonry-grid { column-count: 1; } }
    `}</style>
    <Header></Header>
    <AccessRestriction>
    <div className="container-fluid mt--7">
      <TrialBanner />
      <BoardsCountdownBanner />
      <AddonCrossSellBanner />
      <Row>
        <Col lg="12">
          <Card className="shadow">
            <CardHeader>
              <div className="">
                <h2 className=" mb-1">Create New Test</h2>
                <p className=" mb-0 small">
                  Configure your practice test settings and select questions
                </p>
              </div>
            </CardHeader>

            <CardBody>
              <Form onSubmit={handleCreateTest}>
                {/* Row 1: Test Name + Test Mode */}
                <Row>
                  {/* Test Name */}
                  <Col lg="6" className="mb-4">
                    <h4 className="mb-3">
                      <i className="fas fa-edit mr-2 text-primary"></i>
                      Test Name
                    </h4>
                    <FormGroup className="mb-0">
                      <Input
                        type="text"
                        id="testNameInput"
                        placeholder="e.g., Biology Practice #1"
                        value={testName}
                        onChange={(e) => setTestName(e.target.value)}
                        maxLength={100}
                      />
                      <small className="text-muted">
                        Optional - helps you identify this test later
                      </small>
                    </FormGroup>
                  </Col>

                  {/* Test Mode */}
                  <Col lg="6" className="mb-4">
                    <h4 className="mb-3">
                      <i className="fas fa-cog mr-2 text-primary"></i>
                      Test Mode
                    </h4>
                    <Row>
                      <Col xs="6">
                        <div className="border rounded p-3 h-100">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <i className={`fas fa-chalkboard-teacher mr-2 ${isTutor ? 'text-primary' : 'text-muted'}`}></i>
                              <strong>Tutor</strong>
                            </div>
                            <div className="custom-control custom-switch mb-0">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="tutorSwitch"
                                checked={isTutor}
                                onChange={(e) => setIsTutor(e.target.checked)}
                              />
                              <label className="custom-control-label" htmlFor="tutorSwitch"></label>
                            </div>
                          </div>
                          <small className="text-muted d-block mt-2">
                            See answers after each question
                          </small>
                        </div>
                      </Col>
                      <Col xs="6">
                        <div className="border rounded p-3 h-100">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <i className={`fas fa-clock mr-2 ${isTimed ? 'text-primary' : 'text-muted'}`}></i>
                              <strong>Timed</strong>
                            </div>
                            <div className="custom-control custom-switch mb-0">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="timedSwitch"
                                checked={isTimed}
                                onChange={(e) => setIsTimed(e.target.checked)}
                              />
                              <label className="custom-control-label" htmlFor="timedSwitch"></label>
                            </div>
                          </div>
                          <small className="text-muted d-block mt-2">
                            Set a time limit
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Time Accommodation - Only show when timed */}
                {isTimed && (
                  <div className="mb-4">
                    <h4 className="mb-3">
                      <i className="fas fa-hourglass-half mr-2 text-primary"></i>
                      Time Accommodation
                    </h4>
                    <Row>
                      {[
                        { value: 1.0, label: '1.0x', desc: 'Standard' },
                        { value: 1.25, label: '1.25x', desc: '+25% time' },
                        { value: 1.5, label: '1.5x', desc: '+50% time' },
                        { value: 2.0, label: '2.0x', desc: 'Double time' }
                      ].map((option) => (
                        <Col xs="6" sm="3" key={option.value} className="mb-2 mb-sm-0">
                          <div
                            className={`border rounded p-3 text-center cursor-pointer ${timeAccommodation === option.value ? 'border-primary bg-primary' : ''}`}
                            onClick={() => setTimeAccommodation(option.value)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className={`font-weight-bold h4 mb-1 ${timeAccommodation === option.value ? 'text-white' : ''}`}>{option.label}</div>
                            <small className={timeAccommodation === option.value ? 'text-white' : 'text-muted'}>{option.desc}</small>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                <hr className="my-4" />

                {/* Question Selection Mode */}
                <div className="mb-4">
                  <h4 className="mb-3">
                    <i className="fas fa-list-alt mr-2 text-primary"></i>
                    Question Selection
                  </h4>
                  <Row>
                    <Col xs="6">
                      <div
                        className={`border rounded p-3 h-100 ${selectionMode === 'standard' ? 'border-primary' : ''}`}
                        onClick={() => setSelectionMode('standard')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="custom-control custom-radio mb-0 mr-2">
                            <input
                              className="custom-control-input"
                              id="selectionStandard"
                              type="radio"
                              name="selectionMode"
                              checked={selectionMode === 'standard'}
                              onChange={() => setSelectionMode('standard')}
                            />
                            <label className="custom-control-label" htmlFor="selectionStandard"></label>
                          </div>
                          <div>
                            <strong>Standard</strong>
                            <small className="d-block text-muted">Select by subject & status</small>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs="6">
                      <div
                        className={`border rounded p-3 h-100 ${selectionMode === 'custom' ? 'border-primary' : ''}`}
                        onClick={() => setSelectionMode('custom')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="custom-control custom-radio mb-0 mr-2">
                            <input
                              className="custom-control-input"
                              id="selectionCustom"
                              type="radio"
                              name="selectionMode"
                              checked={selectionMode === 'custom'}
                              onChange={() => setSelectionMode('custom')}
                            />
                            <label className="custom-control-label" htmlFor="selectionCustom"></label>
                          </div>
                          <div>
                            <strong>Custom</strong>
                            <small className="d-block text-muted">Use specific question IDs</small>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {selectionMode === 'standard' && (
                  <>
                    {/* Status Filters */}
                    <div className="mb-4">
                      <h4 className="mb-3">
                        <i className="fas fa-filter mr-2 text-primary"></i>
                        Question Status
                      </h4>
                      <Row>
                        {[
                          { key: 'unused', label: 'Unused', icon: 'fa-circle' },
                          { key: 'used', label: 'Used', icon: 'fa-history' },
                          { key: 'incorrect', label: 'Incorrect', icon: 'fa-times-circle' },
                          { key: 'marked', label: 'Marked', icon: 'fa-flag' },
                          { key: 'omitted', label: 'Omitted', icon: 'fa-minus-circle' },
                          { key: 'correct', label: 'Correct', icon: 'fa-check-circle' }
                        ].map((status) => (
                          <Col xs="6" md="4" lg="auto" key={status.key} className="mb-2">
                            <div className="custom-control custom-checkbox">
                              <input
                                className="custom-control-input"
                                id={`status-${status.key}`}
                                type="checkbox"
                                checked={questionStatus[status.key]}
                                onChange={() => handleStatusToggle(status.key)}
                                disabled={questionCounts.byFilter[status.key] === 0}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor={`status-${status.key}`}
                              >
                                <i className={`fas ${status.icon} mr-1`}></i>
                                {status.label}
                                <Badge color="info" pill className="ml-2">
                                  {questionCounts.byFilter[status.key]}
                                </Badge>
                              </label>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>

                    {/* Tag Filter */}
                    <div className="mb-4 mt-3">
                      <h4 className="mb-3">
                        <i className="fas fa-tags mr-2 text-primary"></i>
                        Tags
                        {selectedTags.length > 0 && (
                          <Badge color="primary" pill className="ml-2">{selectedTags.length} selected</Badge>
                        )}
                      </h4>
                      <p className="text-muted small mb-2">
                        <i className="fas fa-info-circle mr-1"></i>
                        Filter questions by tags (e.g., "shoulder", "rotator cuff"). Leave empty to include all.
                      </p>
                      {/* Selected tags */}
                      {selectedTags.length > 0 && (
                        <div className="mb-2">
                          {selectedTags.map((tag, index) => (
                            <Badge key={index} color="info" className="mr-2 mb-2" style={{ fontSize: '14px' }}>
                              {tag}
                              <Button
                                close
                                size="sm"
                                className="ml-2"
                                onClick={() => handleRemoveTagFilter(tag)}
                              />
                            </Badge>
                          ))}
                          <Button
                            color="link"
                            size="sm"
                            className="text-muted p-0 ml-1"
                            onClick={() => setSelectedTags([])}
                          >
                            Clear all
                          </Button>
                        </div>
                      )}
                      {/* Search input with dropdown */}
                      <div ref={tagDropdownRef} style={{ position: 'relative' }}>
                        <Input
                          type="text"
                          placeholder="Click to browse tags or type to search..."
                          value={tagSearchInput}
                          onChange={(e) => {
                            setTagSearchInput(e.target.value);
                            setIsTagDropdownOpen(true);
                          }}
                          onFocus={() => setIsTagDropdownOpen(true)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (filteredAvailableTags.length > 0) {
                                handleAddTagFilter(filteredAvailableTags[0]);
                              }
                            }
                          }}
                        />
                        {isTagDropdownOpen && (
                          <div
                            className="border rounded shadow-sm bg-white"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              marginTop: '2px'
                            }}
                          >
                            {filteredAvailableTags.length > 0 ? (
                              filteredAvailableTags.map(tag => (
                                <div
                                  key={tag}
                                  className="px-3 py-2 d-flex align-items-center justify-content-between"
                                  style={{ cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                  onClick={() => handleAddTagFilter(tag)}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <span>{tag}</span>
                                  <i className="fas fa-plus text-muted" style={{ fontSize: '0.75rem' }}></i>
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-3 text-muted text-center small">
                                {tagSearchInput.trim() ? `No tags matching "${tagSearchInput}"` : 'No tags available'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Quality Filter hidden for now
                    <hr className="my-4" />

                    <div className="mb-4">
                      <h4 className="mb-3">
                        <i className="fas fa-star mr-2 text-primary"></i>
                        Question Quality
                      </h4>
                      <Row>
                        {[
                          { key: 'excellent', label: 'Excellent', icon: 'fa-gem' },
                          { key: 'simple', label: 'Simple', icon: 'fa-pencil-alt' }
                        ].map((quality) => (
                          <Col xs="6" md="4" lg="auto" key={quality.key} className="mb-2">
                            <div className="custom-control custom-checkbox">
                              <input
                                className="custom-control-input"
                                id={`quality-${quality.key}`}
                                type="checkbox"
                                checked={questionQuality[quality.key]}
                                onChange={() => setQuestionQuality(prev => ({
                                  ...prev,
                                  [quality.key]: !prev[quality.key]
                                }))}
                                disabled={questionCounts.byQuality[quality.key] === 0}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor={`quality-${quality.key}`}
                              >
                                <i className={`fas ${quality.icon} mr-1`}></i>
                                {quality.label}
                                <Badge color="info" pill className="ml-2">
                                  {questionCounts.byQuality[quality.key]}
                                </Badge>
                              </label>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                    */}

                    <hr className="my-4" />

                    {/* Subjects, Units & Topics */}
                    <div className="mb-4">
                      <h4 className="mb-3">
                        <i className="fas fa-book mr-2 text-primary"></i>
                        Subjects, Units & Topics
                      </h4>
                      <div className="subject-masonry-grid">
                        {subjects.map(subject => {
                          const subjectCount = questionCounts.bySubject[subject.subjectId] || 0;
                          const subjectUnits = units.filter(unit => unit.subjectId === subject.subjectId);
                          const subjectDisplayName = getDisplayName(subject, activeSpecialty);

                          // Check if ALL units and topics under this subject are selected
                          const subjectUnitsWithQuestions = subjectUnits.filter(u => (questionCounts.byUnit[u.unitId] || 0) > 0);
                          const subjectTopics = topics?.filter(t => t.subjectId === subject.subjectId) || [];
                          const subjectTopicsWithQuestions = subjectTopics.filter(t => (questionCounts.byTopic?.[t.topicId] || 0) > 0);

                          const allUnitsSelected = subjectUnitsWithQuestions.length > 0 &&
                            subjectUnitsWithQuestions.every(u => selectedUnits.includes(u.unitId));
                          const allTopicsSelected = subjectTopicsWithQuestions.length === 0 ||
                            subjectTopicsWithQuestions.every(t => selectedTopics.includes(t.topicId));

                          // Subject is "selected" only if ALL its units and topics are selected
                          const isAllSelected = allUnitsSelected && allTopicsSelected;
                          // Subject has "some" selections (for visual feedback like border)
                          const hasSomeSelected = subjectUnitsWithQuestions.some(u => selectedUnits.includes(u.unitId)) ||
                            subjectTopicsWithQuestions.some(t => selectedTopics.includes(t.topicId));

                          return (
                            <div key={subject.subjectId} style={{ breakInside: 'avoid', marginBottom: '1rem' }}>
                              <Card className={`mb-0 ${hasSomeSelected ? 'border-primary' : ''}`}>
                                <div
                                  className="p-3 d-flex align-items-center justify-content-between"
                                  onClick={() => subjectCount > 0 && handleSubjectToggle(subject.subjectId)}
                                  style={{ cursor: subjectCount > 0 ? 'pointer' : 'not-allowed', opacity: subjectCount === 0 ? 0.7 : 1 }}
                                >
                                  <div className="d-flex align-items-center">
                                    <div className="custom-control custom-checkbox mb-0 mr-2" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        className="custom-control-input"
                                        id={`subject-${subject.subjectId}`}
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={() => handleSubjectToggle(subject.subjectId)}
                                        disabled={subjectCount === 0}
                                      />
                                      <label className="custom-control-label" htmlFor={`subject-${subject.subjectId}`}></label>
                                    </div>
                                    <strong>{subjectDisplayName}</strong>
                                  </div>
                                  <Badge color={hasSomeSelected ? 'primary' : subjectCount === 0 ? 'secondary' : 'primary' } pill>
                                    {subjectCount}
                                  </Badge>
                                </div>

                                {subjectUnits.length > 0 && (
                                  <div className="p-3 border-top">
                                    {subjectUnits.map(unit => {
                                      const unitCount = questionCounts.byUnit[unit.unitId] || 0;
                                      const unitDisplayName = getDisplayName(unit, activeSpecialty);
                                      const unitTopics = topics?.filter(topic => topic.unitId === unit.unitId) || [];

                                      // Check if ALL topics under this unit are selected
                                      const unitTopicsWithQuestions = unitTopics.filter(t => (questionCounts.byTopic?.[t.topicId] || 0) > 0);
                                      const allUnitTopicsSelected = unitTopicsWithQuestions.length === 0 ||
                                        unitTopicsWithQuestions.every(t => selectedTopics.includes(t.topicId));

                                      // Unit is "selected" only if ALL its topics are selected (or it has no topics)
                                      // Unit checkbox is checked only if ALL its topics are selected
                                      const isUnitAllSelected = selectedUnits.includes(unit.unitId) && allUnitTopicsSelected;

                                      return (
                                        <div key={unit.unitId} className="mb-3">
                                          <div className="d-flex align-items-center">
                                            <div className="custom-control custom-checkbox mb-0">
                                              <input
                                                className="custom-control-input"
                                                id={`unit-${unit.unitId}`}
                                                type="checkbox"
                                                checked={isUnitAllSelected}
                                                onChange={() => handleUnitToggle(unit.unitId)}
                                                disabled={unitCount === 0}
                                              />
                                              <label
                                                className="custom-control-label"
                                                htmlFor={`unit-${unit.unitId}`}
                                                style={{ opacity: unitCount === 0 ? 0.8 : 1, fontWeight: 600 }}
                                              >
                                                {unitDisplayName}
                                                <Badge color={unitCount === 0 ? 'light' : 'warning'} pill className="ml-2">
                                                  {unitCount}
                                                </Badge>
                                              </label>
                                            </div>
                                            {unitTopics.length > 0 && (
                                              <Button
                                                size="sm"
                                                color="link"
                                                className="ml-2 p-0 text-muted"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setExpandedUnits(prev =>
                                                    prev.includes(unit.unitId)
                                                      ? prev.filter(id => id !== unit.unitId)
                                                      : [...prev, unit.unitId]
                                                  );
                                                }}
                                                title={expandedUnits.includes(unit.unitId) ? "Hide topics" : "Show topics"}
                                              >
                                                <i className={`fas fa-chevron-${expandedUnits.includes(unit.unitId) ? 'up' : 'down'} fa-sm`}></i>
                                                <span className="ml-1" style={{ fontSize: '0.75rem' }}>
                                                  {unitTopics.length} topics
                                                </span>
                                              </Button>
                                            )}
                                          </div>
                                          {/* Topics under this unit */}
                                          {unitTopics.length > 0 && expandedUnits.includes(unit.unitId) && (
                                            <Row className="ml-4 mt-2">
                                              {unitTopics.map(topic => {
                                                const topicCount = questionCounts.byTopic[topic.topicId] || 0;
                                                const isTopicSelected = selectedTopics.includes(topic.topicId);
                                                const topicDisplayName = getDisplayName(topic, activeSpecialty);

                                                return (
                                                  <Col xs="6" key={topic.topicId} className="mb-1">
                                                    <div className="custom-control custom-checkbox">
                                                      <input
                                                        className="custom-control-input"
                                                        id={`topic-${topic.topicId}`}
                                                        type="checkbox"
                                                        checked={isTopicSelected}
                                                        onChange={() => handleTopicToggle(topic.topicId)}
                                                        disabled={topicCount === 0}
                                                      />
                                                      <label
                                                        className="custom-control-label"
                                                        htmlFor={`topic-${topic.topicId}`}
                                                        style={{ opacity: topicCount === 0 ? 0.8 : 1, fontSize: '0.9rem' }}
                                                      >
                                                        {topicDisplayName}
                                                        <Badge color={topicCount === 0 ? 'light' : 'info'} pill className="ml-2">
                                                          {topicCount}
                                                        </Badge>
                                                      </label>
                                                    </div>
                                                  </Col>
                                                );
                                              })}
                                            </Row>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <hr className="my-4" />

                    {/* Number of Questions */}
                    <div className="mb-4">
                      <h4 className="mb-3">
                        <i className="fas fa-hashtag mr-2 text-primary"></i>
                        Number of Questions
                      </h4>
                      <p className="text-muted small mb-3">
                        <i className="fas fa-info-circle mr-1"></i>
                        Select the number of questions for your test block (max 60 per block).
                      </p>
                      <Row className="align-items-center">
                        <Col md="4">
                          <FormGroup className="mb-0">
                            <Input
                              type="number"
                              min="1"
                              max={dynamicMaxQuestions}
                              value={dynamicMaxQuestions === 0 ? 0 : numberOfQuestions}
                              onClick={(e) => e.target.select()}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                // Prevent e, E, +, -, . characters in number input
                                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || val === null) {
                                  setNumberOfQuestions('');
                                } else {
                                  const num = parseInt(val);
                                  if (!isNaN(num) && num >= 0) {
                                    setNumberOfQuestions(num);
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                // On blur, ensure we have a valid value (at least 1)
                                if (numberOfQuestions === '' || numberOfQuestions < 1) {
                                  setNumberOfQuestions(1);
                                } else if (numberOfQuestions > dynamicMaxQuestions && dynamicMaxQuestions > 0) {
                                  setNumberOfQuestions(dynamicMaxQuestions);
                                }
                              }}
                              disabled={dynamicMaxQuestions === 0}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <span className="text-muted h5">Max available: </span>
                          <Badge color="warning" pill >
                            {dynamicMaxQuestions}
                          </Badge>
                        </Col>
                      </Row>
                    </div>
                  </>
                )}

                {selectionMode === 'custom' && (
                  <div className="mb-4">
                    {/* Retrieve from Test */}
                    <div className="mb-4">
                      <h4 className="mb-3">
                        <i className="fas fa-download mr-2 text-primary"></i>
                        Retrieve from Previous Test
                      </h4>
                      <Row className="align-items-end">
                        <Col md="5" className="mb-2 mb-md-0">
                          <FormGroup className="mb-0">
                            <Input
                              type="text"
                              placeholder="Enter test ID (e.g., TEST-000000001)"
                              value={testId}
                              onChange={(e) => setTestId(e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <Button
                            color="primary"
                            onClick={handleRetrieveQuestions}
                            disabled={retrievingQuestions || !testId.trim()}
                          >
                            {retrievingQuestions ? (
                              <>
                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                Retrieving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-download mr-2"></i>
                                Retrieve Questions
                              </>
                            )}
                          </Button>
                        </Col>
                      </Row>
                      <small className="text-muted d-block mt-2">
                        Find test numbers in "Previous Tests" section
                      </small>
                    </div>

                    <hr className="my-4" />

                    {/* Manual Question IDs */}
                    <div>
                      <h4 className="mb-3">
                        <i className="fas fa-keyboard mr-2 text-primary"></i>
                        Or Enter Question IDs Manually
                      </h4>
                      <Row>
                        <Col md="8">
                          <FormGroup className="mb-0">
                            <Input
                              type="textarea"
                              rows="3"
                              placeholder="QST-BIO-00001, QST-BIO-00002, QST-CHE-00001"
                              value={customQuestionIds}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^A-Za-z0-9,-]/g, '');
                                setCustomQuestionIds(value);
                              }}
                            />
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <small className="text-muted">
                                Separate IDs with commas. Max {maxQuestionsPerBlock} questions.
                              </small>
                              {customQuestionIds && (
                                <Badge color="info" pill>
                                  {customQuestionIds.split(',').filter(id => id.trim()).length} question(s)
                                </Badge>
                              )}
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}

                <hr className="my-4" />

                {/* Generate Button */}
                <div className="text-center text-md-right">
                  <Button color="primary" size="lg" type="submit">
                    <i className="fas fa-rocket mr-2"></i>
                    Generate Test
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Card className="shadow mt-4 mb-5">
        <CardHeader className="border-0">
          <h3 className="mb-0">
            <i className="fas fa-chart-line text-info mr-2"></i>
            Activity & Streaks
          </h3>
        </CardHeader>
        <CardBody>
          <ActivityStreaksCard testSummaries={cachedTestSummaries} />
        </CardBody>
        <CardFooter className="border-0 text-right">
          <Button color="primary" onClick={() => history.push('/client/question-bank/performance')}>
            View Full Performance <i className="fas fa-arrow-right ml-1"></i>
          </Button>
        </CardFooter>
      </Card>
    </div>

    <Modal
      isOpen={isTestModalOpen}
      toggle={handleCloseTest}
      className="modal-dialog-centered"
      style={{ maxWidth: '100vw', margin: 0, height: '100dvh' }}
      contentClassName="h-100"
    >
      {testLayoutMode === 'standard' ? (
      <div ref={testModalRef} className="d-flex flex-column h-100" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Top Header Bar - Standard Style */}
        <div className="bg-primary text-white px-2 px-md-3 py-2 d-flex justify-content-between align-items-center" style={{ minHeight: '50px' }}>
          {/* Left: PM&R Prep - Student Name */}
          <div className="d-flex align-items-center" style={{ flex: 1 }}>
            {/* Desktop: Full text */}
            <span className="font-weight-bold d-none d-md-inline" style={{ fontSize: '0.9rem' }}>
              {specialtyName} Prep - {userName || 'Student'}
            </span>
            {/* Mobile: Short text */}
            <span className="font-weight-bold d-md-none" style={{ fontSize: '0.85rem' }}>
              {specialtyName} - {userName || 'Student'}
            </span>
          </div>

          {/* Center: Test Info - Hidden on mobile */}
          <div className="d-none d-md-flex flex-column align-items-center justify-content-center text-center" style={{ flex: 1 }}>
            <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
              Test Id: {savedTestId || 'N/A'} ({isTutor ? 'Tutored' : 'Untutored'}, {isTimed ? 'Timed' : 'Untimed'})
            </span>
            <div className="d-flex align-items-center">
              <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                Qid: {currentQuestion?.questionCode || 'N/A'}
              </span>
              {/* Save Status Indicator */}
              {saveStatus !== 'idle' && (
                <span className="ml-2 d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                  {saveStatus === 'saving' && (
                    <>
                      <i className="fas fa-sync fa-spin mr-1" style={{ color: '#ffc107' }}></i>
                      <span style={{ color: '#ffc107' }}>Saving...</span>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <i className="fas fa-check-circle mr-1" style={{ color: '#28a745' }}></i>
                      <span style={{ color: '#28a745' }}>Saved</span>
                    </>
                  )}
                  {saveStatus === 'error' && (
                    <>
                      <i className="fas fa-exclamation-circle mr-1" style={{ color: '#dc3545' }}></i>
                      <span style={{ color: '#dc3545' }}>Save failed</span>
                    </>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Right: Time and Question Counter */}
          <div className="d-flex flex-column align-items-end" style={{ flex: 1 }}>
            <div className="d-flex align-items-center">
              <i className="fas fa-clock mr-1" style={{ fontSize: '0.75rem' }}></i>
              <span className="d-none d-sm-inline" style={{ fontSize: '0.8rem' }}>Time : </span>
              <span className="font-weight-bold ml-1" style={{ fontSize: '0.85rem' }}>
                {formatTime(isReviewMode ? 0 : (isTimed ? timeRemaining : elapsedTime))}
              </span>
            </div>
            <div className="d-flex align-items-center">
              <i className="fas fa-file-alt mr-1" style={{ fontSize: '0.75rem' }}></i>
              <span className="font-weight-bold">{currentQuestionIndex + 1}</span>
              <span className="mx-1">of</span>
              <span>{testQuestions.length}</span>
              {/* Mobile Save Status Indicator */}
              {saveStatus !== 'idle' && (
                <span className="ml-2 d-md-none">
                  {saveStatus === 'saving' && <i className="fas fa-sync fa-spin" style={{ color: '#ffc107', fontSize: '0.75rem' }}></i>}
                  {saveStatus === 'saved' && <i className="fas fa-check-circle" style={{ color: '#28a745', fontSize: '0.75rem' }}></i>}
                  {saveStatus === 'error' && <i className="fas fa-exclamation-circle" style={{ color: '#dc3545', fontSize: '0.75rem' }}></i>}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Toolbar Row - MCAT Style with markup tools and controls */}
        <div className="bg-dark border-bottom px-2 py-1 d-flex justify-content-between align-items-center flex-wrap" style={{ minHeight: '38px' }}>
          {/* Left: Markup Tools */}
          <div className="d-flex align-items-center">
            <input
              type="color"
              value={highlightColor}
              onChange={(e) => setHighlightColor(e.target.value)}
              style={{ width: '20px', height: '20px', border: '1px solid #ccc', cursor: 'pointer', padding: 0 }}
              className="mr-2"
              title="Choose highlight color"
            />
            <Button
              color="primary"
              size="sm"
              className="mr-1 px-2"
              onClick={() => handleTextSelection('highlight')}
              title="Highlight selected text"
              style={{ fontSize: '0.75rem' }}
            >
              <i className="fas fa-highlighter mr-1"></i>
              <span className="d-none d-sm-inline">Highlight</span>
            </Button>
            <Button
              color="primary"
              size="sm"
              className="mr-1 px-2"
              onClick={() => handleTextSelection('strikethrough')}
              title="Strikethrough selected text"
              style={{ fontSize: '0.75rem' }}
            >
              <i className="fas fa-strikethrough mr-1"></i>
              <span className="d-none d-sm-inline">Strikethrough</span>
            </Button>
            <Button
              color="primary"
              size="sm"
              className="px-2 d-none d-md-inline-block"
              onClick={clearAllMarkup}
              title="Clear all markup"
              style={{ fontSize: '0.75rem' }}
            >
              <i className="fas fa-eraser mr-1"></i>
              <span>Clear</span>
            </Button>
          </div>

          {/* Right: Zoom, Flag and Controls */}
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center mr-2">
              <Button
                color="primary"
                size="sm"
                className="pr-2 m-0"
                onClick={() => setFontSizePercent(prev => Math.max(50, prev - 10))}
                title="Zoom out"
                style={{ fontSize: '0.75rem' }}
                disabled={fontSizePercent <= 50}
              >
                <i className="fas fa-search-minus"></i>
              </Button>
              <span className="text-white mx-1" style={{ fontSize: '0.7rem', minWidth: '32px', textAlign: 'center' }}>{fontSizePercent}%</span>
              <Button
                color="primary"
                size="sm"
                className="px-2"
                onClick={() => setFontSizePercent(prev => Math.min(200, prev + 10))}
                title="Zoom in"
                style={{ fontSize: '0.75rem' }}
                disabled={fontSizePercent >= 200}
              >
                <i className="fas fa-search-plus"></i>
              </Button>
            </div>
            <Button
              color="warning"
              size="sm"
              onClick={() => setIsFeedbackModalOpen(true)}
              className="px-2 mr-2"
              style={{ fontSize: '0.75rem' }}
              title="Submit feedback for this question"
            >
              <i className="fas fa-comment-alt mr-1"></i>
              <span className="d-none d-sm-inline">Feedback</span>
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={handleMarkQuestion}
              className="px-2 mr-2"
              style={{ fontSize: '0.75rem' }}
            >
              <i className={`fas fa-flag ${markedQuestions.includes(currentQuestion?.questionId) ? 'text-danger' : ''} mr-1`}></i>
              <span className="d-none d-sm-inline">Flag for Review</span>
            </Button>
          </div>
        </div>

        {/* Layout Toggle */}
        <div className="bg-light px-2 pt-1 pb-2 d-flex justify-content-end align-items-center" style={{ minHeight: '32px' }}>
          <div className="d-flex align-items-center">
            <span className="text-dark mr-2 d-none d-sm-inline" style={{ fontSize: '0.75rem' }}>Layout:</span>
            <div className="btn-group btn-group-sm">
              <Button
                color={testLayoutMode === 'standard' ? 'dark' : 'outline-dark'}
                size="sm"
                onClick={() => setTestLayoutMode('standard')}
                style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
              >
                Prep Style
              </Button>
              <Button
                color={testLayoutMode === 'pmr' ? 'dark' : 'outline-dark'}
                size="sm"
                onClick={() => setTestLayoutMode('pmr')}
                style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
              >
                Board Style
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-grow-1 overflow-hidden bg-white exam-content-font" style={{ zoom: fontSizePercent / 100 }}>
          <Row className="h-100 no-gutters">
            {/* Left Column - Passage (only show for passage-based questions on lg+) */}
            {currentQuestion?.passageData && (
              <Col lg="6" className="h-100 d-none d-lg-flex flex-column" style={{ borderRight: '6px solid #6c757d' }}>
                <div ref={passageContentRef} className="h-100 d-flex flex-column bg-white overflow-auto p-3 p-md-4">
                  {/* Passage Title - MCAT Style */}
                  <h5 className="font-weight-bold text-dark mb-3" style={{ fontSize: '1.1rem' }}>
                    Passage {(() => {
                      const uniquePassageIds = [...new Set(testQuestions.filter(q => q.passageData).map(q => q.passageData.passageId))];
                      return uniquePassageIds.indexOf(currentQuestion.passageData.passageId) + 1;
                    })()}
                    {currentQuestion.passageQuestionNumber && (
                      <span> (Questions {currentQuestion.passageQuestionNumber}-{currentQuestion.totalPassageQuestions})</span>
                    )}
                  </h5>
                  <div
                    key={`passage-${currentQuestion.questionId}`}
                    id="passage-content"
                    className="passage-text text-dark flex-grow-1"
                    style={{
                      fontSize: '0.95rem',
                      lineHeight: '1.7',
                      userSelect: 'text'
                    }}
                    dangerouslySetInnerHTML={{ __html: renderPassagePreview(currentQuestion.passageData.passageText) }}
                  />
                </div>
              </Col>
            )}

            {/* Spacer for standalone questions (2-8-2 layout) */}
            {!currentQuestion?.passageData && (
              <Col lg="2" className="d-none d-lg-block"></Col>
            )}

            {/* Question Column - 8 cols for standalone, 6 cols for passage-based */}
            <Col xs="12" lg={currentQuestion?.passageData ? "6" : "8"} className="h-100 d-flex flex-column bg-white" style={{ minHeight: 0 }}>
              <div ref={questionContentRef} className="flex-grow-1 overflow-auto p-3 p-md-4" style={{ minHeight: 0 }}>
                {currentQuestion ? (
                  <>
                    {/* Mobile Passage Toggle - Only show on mobile when there's a passage */}
                    {currentQuestion?.passageData && (
                      <div className="mb-3 d-lg-none">
                        {/* Passage Title - same as desktop */}
                        <div
                          className="d-flex align-items-center justify-content-between mb-2"
                          onClick={() => setIsMobilePassageVisible(!isMobilePassageVisible)}
                          style={{ cursor: 'pointer' }}
                        >
                          <h5 className="font-weight-bold text-dark mb-0" style={{ fontSize: '1.1rem' }}>
                            Passage {(() => {
                              const uniquePassageIds = [...new Set(testQuestions.filter(q => q.passageData).map(q => q.passageData.passageId))];
                              return uniquePassageIds.indexOf(currentQuestion.passageData.passageId) + 1;
                            })()}
                            {currentQuestion.passageQuestionNumber && (
                              <span> (Questions {currentQuestion.passageQuestionNumber}-{currentQuestion.totalPassageQuestions})</span>
                            )}
                          </h5>
                          <i className={`fas fa-chevron-${isMobilePassageVisible ? 'up' : 'down'} text-muted`}></i>
                        </div>
                        {isMobilePassageVisible && (
                          <div className="position-relative">
                            <div
                              key={`passage-mobile-${currentQuestion.questionId}`}
                              id="passage-content-mobile"
                              className="passage-text text-dark p-2 border rounded-top"
                              style={{
                                fontSize: '0.9rem',
                                lineHeight: '1.6',
                                height: `${mobilePassageHeight}px`,
                                overflow: 'auto',
                                userSelect: 'text',
                                borderBottom: 'none'
                              }}
                              dangerouslySetInnerHTML={{ __html: renderPassagePreview(currentQuestion.passageData.passageText) }}
                            />
                            {/* Resize handle - drag from center */}
                            <div
                              className="d-flex align-items-center justify-content-center border rounded-bottom"
                              style={{
                                backgroundColor: '#e9ecef',
                                padding: '10px 0',
                                cursor: 'ns-resize',
                                touchAction: 'none'
                              }}
                              onMouseDown={handlePassageResizeStart}
                              onTouchStart={handlePassageResizeStart}
                            >
                              <div
                                style={{
                                  width: '60px',
                                  height: '6px',
                                  backgroundColor: '#adb5bd',
                                  borderRadius: '3px'
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Question Header - MCAT Style */}
                    <h5 className="font-weight-bold text-dark mb-3" style={{ fontSize: '1.1rem' }}>
                      Question {currentQuestionIndex + 1}
                    </h5>

                    {/* Question Text */}
                    <div className="mb-4" key={`q-text-${currentQuestion.questionId}`}>
                      <div
                        id="question-content"
                        className="question-text text-dark"
                        style={{ fontSize: '0.95rem', lineHeight: '1.7' }}
                        dangerouslySetInnerHTML={{ __html: renderQuestionText(currentQuestion.questionText) || 'Question text not available' }}
                      />
                    </div>

                    {/* Answer Options - MCAT Style */}
                    <div className="answer-options text-dark" key={`q-answers-${currentQuestion.questionId}`}>
                      {currentQuestion.answers && currentQuestion.answers.map((answer, index) => {
                        const isSelected = selectedAnswers[currentQuestion.questionId] === answer.answerId;
                        const isCorrectAnswer = answer.isCorrect;
                        const showResults = isReviewMode || (isTutor && showExplanation);

                        // Calculate answer choice percentage
                        const answerDistribution = currentQuestion.answerDistribution || {};
                        const totalResponses = Object.values(answerDistribution).reduce((sum, count) => sum + count, 0);
                        const chosenCount = answerDistribution[answer.answerId] || 0;
                        const chosenPercentage = totalResponses > 0 ? Math.round((chosenCount / totalResponses) * 100) : 0;

                        let bgClass = '';
                        if (showResults && isSelected) {
                          bgClass = 'bg-lighter';
                        } else if (isSelected) {
                          bgClass = 'bg-lighter';
                        }

                        return (
                          <div
                            key={answer.answerId}
                            className={`d-flex align-items-start p-2 rounded ${bgClass}`}
                            style={{ cursor: (showExplanation || isReviewMode) ? 'default' : 'pointer' }}
                            onClick={() => !showExplanation && !isReviewMode && handleSelectAnswer(currentQuestion.questionId, answer.answerId)}
                          >
                            {/* Check/X icon for results */}
                            <span style={{ width: '20px', minWidth: '20px', marginTop: '2px', textAlign: 'center' }}>
                              {showResults && isCorrectAnswer && (
                                <span style={{ color: '#28a745', fontWeight: '700', fontSize: '1rem' }}>&#10003;</span>
                              )}
                              {showResults && isSelected && !isCorrectAnswer && (
                                <span style={{ color: '#dc3545', fontWeight: '700', fontSize: '1rem' }}>&#10007;</span>
                              )}
                            </span>
                            <input
                              type="radio"
                              name={`question-${currentQuestion.questionId}`}
                              value={answer.answerId}
                              checked={isSelected}
                              readOnly
                              style={{ marginRight: '6px', marginTop: '4px', cursor: (showExplanation || isReviewMode) ? 'default' : 'pointer', pointerEvents: 'none' }}
                            />
                            <span style={{ fontSize: '0.9rem', color: '#333', minWidth: '24px' }}>
                              {String.fromCharCode(65 + index)}.
                            </span>
                            <span
                              id={`answer-${answer.answerId}`}
                              style={{ fontSize: '0.9rem', color: '#333' }}
                              dangerouslySetInnerHTML={{ __html: answer.answerHtml || renderChoiceText(answer.answerText) }}
                            />
                            {/* Show percentage of users who chose this answer (only if 5+ responses) */}
                            {showResults && totalResponses >= 5 && (
                              <span
                                className={`ml-2 ${isCorrectAnswer ? 'text-success' : 'text-muted'}`}
                                style={{ fontSize: '0.75rem', fontWeight: '500', whiteSpace: 'nowrap' }}
                              >
                                <i className="fas fa-users mr-1"></i>
                                {chosenPercentage}%
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Submit Button */}
                    {!showExplanation && selectedAnswers[currentQuestion.questionId] && isTutor && !isReviewMode && (
                      <div className="mt-3">
                        <Button
                          color="primary"
                          onClick={handleSubmitAnswer}
                          block
                          size="sm"
                          className="py-2"
                        >
                          <i className="fas fa-check mr-2"></i>
                          Submit Answer
                        </Button>
                      </div>
                    )}

                    {/* Review Mode Result Box */}
                    {isReviewMode && (() => {
                      const userSelectedAnswerId = selectedAnswers[currentQuestion.questionId];
                      const wasOmitted = !userSelectedAnswerId;
                      const selectedAnswer = currentQuestion.answers?.find(a => a.answerId === userSelectedAnswerId);
                      const isCorrect = selectedAnswer?.isCorrect;
                      const correctIndex = currentQuestion.answers?.findIndex(a => a.isCorrect);

                      const timesCorrect = currentQuestion.timesCorrect || 0;
                      const totalAnswered = Object.values(currentQuestion.answerDistribution || {}).reduce((sum, c) => sum + c, 0);
                      const correctPercentage = totalAnswered >= 5
                        ? Math.round((timesCorrect / totalAnswered) * 100)
                        : 'N/A';

                      let timeSpentFormatted = 'N/A';
                      const questionTime = questionTimeTracking[currentQuestion.questionId] || 0;
                      if (questionTime > 0) {
                        const mins = Math.floor(questionTime / 60);
                        const secs = questionTime % 60;
                        timeSpentFormatted = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                      }

                      const statusColor = wasOmitted ? 'warning' : (isCorrect ? 'success' : 'danger');
                      const statusText = wasOmitted ? 'Omitted' : (isCorrect ? 'Correct' : 'Incorrect');
                      const statusIcon = wasOmitted ? 'fa-minus' : (isCorrect ? 'fa-check' : 'fa-times');

                      return (
                        <div className={`mt-4 p-3 rounded border border-${statusColor}`}>
                          <div className="d-flex align-items-center mb-2">
                            <div className={`rounded-circle d-flex align-items-center justify-content-center mr-2 bg-${statusColor}`} style={{ width: '28px', height: '28px', minWidth: '28px' }}>
                              <i className={`fas ${statusIcon} text-white`} style={{ fontSize: '0.75rem' }}></i>
                            </div>
                            <h5 className={`mb-0 font-weight-bold text-${statusColor}`} style={{ fontSize: '1rem' }}>
                              {statusText}
                            </h5>
                          </div>
                          <div className="row no-gutters">
                            <div className="col-4 px-1 mb-2">
                              <div className="text-center p-1 border rounded bg-lighter">
                                <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '0.5rem', letterSpacing: '0.3px' }}>Correct Answer</small>
                                <span className="text-dark font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                  {correctIndex >= 0 ? String.fromCharCode(65 + correctIndex) : 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="col-4 px-1 mb-2">
                              <div className="text-center p-1 border rounded bg-lighter">
                                <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '0.5rem', letterSpacing: '0.3px' }}>Correct %</small>
                                <span className="text-dark font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                  {correctPercentage === 'N/A' ? 'N/A' : `${correctPercentage}%`}
                                </span>
                              </div>
                            </div>
                            <div className="col-4 px-1 mb-2">
                              <div className="text-center p-1 border rounded bg-lighter">
                                <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '0.5rem', letterSpacing: '0.3px' }}>Time Spent</small>
                                <span className="text-dark font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                  {timeSpentFormatted}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Review Mode Explanation */}
                    {isReviewMode && currentQuestion.explanation && (
                      <div className="mt-4 p-3 border rounded bg-lighter">
                        <h6 className="text-primary mb-2 font-weight-bold" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-lightbulb mr-2"></i>Explanation
                        </h6>
                        <div key={`expl-${currentQuestion.questionId}`} id="explanation-content" className="text-dark" style={{ fontSize: '0.85rem' }} dangerouslySetInnerHTML={{ __html: renderExplanationText(currentQuestion.explanation) }} />
                      </div>
                    )}

                    {/* Tutor Mode Result Box */}
                    {isTutor && showExplanation && selectedAnswers[currentQuestion.questionId] && !isReviewMode && (() => {
                      const selectedAnswer = currentQuestion.answers.find(a => a.answerId === selectedAnswers[currentQuestion.questionId]);
                      const isCorrect = selectedAnswer?.isCorrect;
                      const correctIndex = currentQuestion.answers.findIndex(a => a.isCorrect);

                      const timesCorrect = currentQuestion.timesCorrect || 0;
                      const totalAnswered = Object.values(currentQuestion.answerDistribution || {}).reduce((sum, c) => sum + c, 0);
                      const correctPercentage = totalAnswered >= 5
                        ? Math.round((timesCorrect / totalAnswered) * 100)
                        : 'N/A';

                      let timeSpentFormatted = 'N/A';
                      const questionTime = questionTimeTracking[currentQuestion.questionId] || 0;
                      if (questionTime > 0) {
                        const mins = Math.floor(questionTime / 60);
                        const secs = questionTime % 60;
                        timeSpentFormatted = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                      }

                      return (
                        <div className={`mt-4 p-3 rounded border ${isCorrect ? 'border-success' : 'border-danger'}`}>
                          <div className="d-flex align-items-center mb-2">
                            <div className={`rounded-circle d-flex align-items-center justify-content-center mr-2 ${isCorrect ? 'bg-success' : 'bg-danger'}`} style={{ width: '28px', height: '28px', minWidth: '28px' }}>
                              <i className={`fas ${isCorrect ? 'fa-check' : 'fa-times'} text-white`} style={{ fontSize: '0.75rem' }}></i>
                            </div>
                            <h5 className={`mb-0 font-weight-bold ${isCorrect ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1rem' }}>
                              {isCorrect ? 'Correct' : 'Incorrect'}
                            </h5>
                          </div>
                          <div className="row no-gutters">
                            <div className="col-4 px-1 mb-2">
                              <div className="text-center p-1 border rounded bg-lighter">
                                <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '0.5rem', letterSpacing: '0.3px' }}>Correct Answer</small>
                                <span className="text-dark font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                  {String.fromCharCode(65 + correctIndex)}
                                </span>
                              </div>
                            </div>
                            <div className="col-4 px-1 mb-2">
                              <div className="text-center p-1 border rounded bg-lighter">
                                <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '0.5rem', letterSpacing: '0.3px' }}>Correct %</small>
                                <span className="text-dark font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                  {correctPercentage === 'N/A' ? 'N/A' : `${correctPercentage}%`}
                                </span>
                              </div>
                            </div>
                            <div className="col-4 px-1 mb-2">
                              <div className="text-center p-1 border rounded bg-lighter">
                                <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '0.5rem', letterSpacing: '0.3px' }}>Time Spent</small>
                                <span className="text-dark font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                  {timeSpentFormatted}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Tutor Mode Explanation */}
                    {isTutor && showExplanation && currentQuestion.explanation && !isReviewMode && (
                      <div className="mt-4 p-3 border rounded bg-lighter">
                        <h6 className="text-primary mb-2 font-weight-bold" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-lightbulb mr-2"></i>Explanation
                        </h6>
                        <div key={`expl-tutor-${currentQuestion.questionId}`} id="explanation-content-tutor" className="text-dark" style={{ fontSize: '0.85rem' }} dangerouslySetInnerHTML={{ __html: renderExplanationText(currentQuestion.explanation) }} />
                      </div>
                    )}

                    {/* AI Tutor Chat - Standard Layout */}
                    {(showExplanation || isReviewMode) && (
                      <TutoredModeAIChat
                        key={currentQuestion.questionId}
                        questionText={currentQuestion.questionText}
                        explanationText={currentQuestion.explanation}
                        answers={currentQuestion.answers}
                        correctAnswerId={currentQuestion.answers?.find(a => a.isCorrect)?.answerId}
                        userAnswerId={selectedAnswers[currentQuestion.questionId]}
                        passageText={currentQuestion.passageData?.passageText}
                        testId={savedTestId}
                        questionId={currentQuestion.questionId}
                        variant="standard"
                      />
                    )}
                  </>
                ) : (
                  <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted">
                    <i className="fas fa-spinner fa-spin fa-2x mb-2"></i>
                    <p className="mb-0" style={{ fontSize: '0.9rem' }}>Loading...</p>
                  </div>
                )}
                {/* Scroll indicator - shows when answer choices are below the fold */}
                <div style={{
                  position: 'sticky',
                  bottom: 0,
                  textAlign: 'center',
                  padding: '6px 0 2px',
                  opacity: canScrollDown ? 1 : 0,
                  transition: 'opacity 0.3s',
                  pointerEvents: 'none'
                }}>
                  <i className="fas fa-angle-double-down scroll-down-indicator" style={{ fontSize: '1.3rem', color: '#adb5bd' }}></i>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Bottom Navigation Bar - Full Width Standard Style */}
        <div className="border-top bg-dark p-2 d-flex justify-content-between align-items-center" style={{ minHeight: '45px' }}>
          <div className="d-flex align-items-center">
            {!isReviewMode && (
              <Button
                color="primary"
                size="sm"
                onClick={handleEndTest}
                className="px-2 px-md-3 mr-2"
                style={{ fontSize: '0.8rem' }}
              >
                <i className="fas fa-stop mr-0"></i>
                <span className="d-none d-md-inline ml-1">End</span>
              </Button>
            )}
            {!isReviewMode && (
              <Button
                color="primary"
                size="sm"
                onClick={handleCloseTest}
                className="px-2 px-md-3 mr-2"
                style={{ fontSize: '0.8rem' }}
              >
                <i className="fas fa-pause mr-0"></i>
                <span className="d-none d-md-inline ml-1">Pause</span>
              </Button>
            )}
            {isReviewMode && (
              <Button
                color="primary"
                size="sm"
                onClick={handleCloseTest}
                className="px-2 px-md-3 mr-2"
                style={{ fontSize: '0.8rem' }}
              >
                <i className="fas fa-times mr-0"></i>
                <span className="d-none d-md-inline ml-1">Close</span>
              </Button>
            )}
{/* Periodic Table button hidden
            <Button
              color="primary"
              size="sm"
              onClick={() => setIsPeriodicTableOpen(true)}
              className="px-2 px-md-3"
              style={{ fontSize: '0.8rem' }}
            >
              <i className="fas fa-flask mr-0"></i>
              <span className="d-none d-md-inline ml-1">Periodic Table</span>
            </Button>
*/}
          </div>

          <div className="d-flex align-items-center">
            <Button
              color="primary"
              size="sm"
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              className="px-2 px-md-3 mr-2"
              style={{ fontSize: '0.8rem' }}
            >
              <i className="fas fa-arrow-left mr-0"></i>
              <span className="d-none d-md-inline ml-1">Previous</span>
            </Button>

            <Button
              color="primary"
              size="sm"
              onClick={() => setIsNavigatorModalOpen(true)}
              className="px-2 px-md-3 mr-2"
              style={{ fontSize: '0.8rem' }}
            >
              <i className="fas fa-compass mr-0"></i>
              <span className="d-none d-md-inline ml-1">Navigator</span>
            </Button>

            <Button
              color="primary"
              size="sm"
              onClick={() => {
                if (currentQuestionIndex === testQuestions.length - 1) {
                  handleEndTest();
                } else {
                  handleNextQuestion();
                }
              }}
              className="px-2 px-md-3"
              style={{ fontSize: '0.8rem' }}
            >
              <span className="d-none d-md-inline mr-1">Next</span>
              <i className="fas fa-arrow-right ml-0"></i>
            </Button>
          </div>
        </div>
      </div>
      ) : (
      /* PM&R Style Layout - Exact Match */
      <div ref={testModalRef} className="d-flex flex-column h-100" style={{ backgroundColor: '#ffffff' }}>
        {/* PM&R Header - Steel Blue */}
        <div className="d-flex justify-content-between align-items-center px-3" style={{ backgroundColor: '#1e6091', minHeight: '36px' }}>
          <div className="d-flex align-items-center">
            <span className="text-white" style={{ fontSize: '0.9rem' }}>
              {specialtyName} - {userName || 'Candidate Name'}
            </span>
            {/* Save Status Indicator - Board Style */}
            {saveStatus !== 'idle' && (
              <span className="ml-3 d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                {saveStatus === 'saving' && (
                  <>
                    <i className="fas fa-sync fa-spin mr-1" style={{ color: '#ffffff' }}></i>
                    <span className="d-none d-sm-inline" style={{ color: '#ffffff' }}>Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <i className="fas fa-check-circle mr-1" style={{ color: '#ffffff' }}></i>
                    <span className="d-none d-sm-inline" style={{ color: '#ffffff' }}>Saved</span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <i className="fas fa-exclamation-circle mr-1" style={{ color: '#dc3545' }}></i>
                    <span className="d-none d-sm-inline" style={{ color: '#dc3545' }}>Save failed</span>
                  </>
                )}
              </span>
            )}
          </div>
          <div className="d-flex flex-column align-items-end text-white" style={{ fontSize: '0.8rem' }}>
            <span>
              <i className="far fa-clock mr-1"></i>
              Time Remaining {formatTime(isReviewMode ? 0 : (isTimed ? timeRemaining : elapsedTime))}
            </span>
            <span>
              <i className="far fa-file-alt mr-1"></i>
              {currentQuestionIndex + 1} of {testQuestions.length}
            </span>
          </div>
        </div>

        {/* PM&R Progress Bar - White */}
        <div style={{ position: 'relative', height: '1px', backgroundColor: '#ffffff', width: '100%' }}></div>

        {/* Flag for Review Row - Sky Blue Bar */}
        <div className="d-flex justify-content-between align-items-center px-3 py-1" style={{ backgroundColor: '#5BA4D9', minHeight: '28px' }}>
          {/* Layout Toggle - Left */}
          <span
            onClick={() => setTestLayoutMode('standard')}
            style={{ cursor: 'pointer', color: '#ffffff', fontSize: '0.8rem' }}
            className="d-flex align-items-center"
          >
            <i className="fas fa-exchange-alt mr-1"></i>
            Switch to Prep Style
          </span>
          {/* Zoom, Feedback & Flag for Review - Right (only show when not in review) */}
          {!showPmrReview && (
            <div className="d-flex align-items-center">
              <span className="d-flex align-items-center mr-3">
                <i
                  className="fas fa-search-minus"
                  onClick={() => fontSizePercent > 50 && setFontSizePercent(prev => prev - 10)}
                  style={{ cursor: fontSizePercent <= 50 ? 'default' : 'pointer', color: fontSizePercent <= 50 ? '#a0c4e8' : '#ffffff', fontSize: '0.8rem' }}
                ></i>
                <span className="mx-1" style={{ color: '#ffffff', fontSize: '0.75rem', minWidth: '32px', textAlign: 'center' }}>{fontSizePercent}%</span>
                <i
                  className="fas fa-search-plus"
                  onClick={() => fontSizePercent < 200 && setFontSizePercent(prev => prev + 10)}
                  style={{ cursor: fontSizePercent >= 200 ? 'default' : 'pointer', color: fontSizePercent >= 200 ? '#a0c4e8' : '#ffffff', fontSize: '0.8rem' }}
                ></i>
              </span>
              <span
                onClick={() => setIsFeedbackModalOpen(true)}
                style={{ cursor: 'pointer', color: '#ffffff', fontSize: '0.85rem', marginRight: '16px' }}
                className="d-flex align-items-center"
              >
                <i className="fas fa-comment-alt mr-1"></i>
                Feedback
              </span>
              <span
                onClick={handleMarkQuestion}
                style={{ cursor: 'pointer', color: '#ffffff', fontSize: '0.85rem' }}
                className="d-flex align-items-center"
              >
                <i className={`far fa-flag mr-1`} style={{ color: markedQuestions.includes(currentQuestion?.questionId) ? '#ffc107' : '#ffffff' }}></i>
                Flag for Review
              </span>
            </div>
          )}
        </div>

        {/* PM&R Main Content Area - Clean White */}
        <div ref={questionContentRef} className="flex-grow-1 overflow-auto exam-content-font" style={{ backgroundColor: '#ffffff', padding: '16px 24px', zoom: fontSizePercent / 100 }}>
          {showPmrReview ? (
            /* PM&R Review Section */
            <>
              <h4 className="text-center mb-4" style={{ color: '#333', fontWeight: '700' }}>Test Review</h4>

              {/* Instructions Section */}
              <div
                style={{ backgroundColor: '#5BA4D9', color: '#ffffff', padding: '8px 12px', marginBottom: pmrInstructionsOpen ? '0' : '12px', cursor: 'pointer' }}
                onClick={() => setPmrInstructionsOpen(!pmrInstructionsOpen)}
              >
                <i className={`fas fa-${pmrInstructionsOpen ? 'minus' : 'plus'}-square mr-2`}></i>
                Instructions
              </div>
              {pmrInstructionsOpen && (
                <div style={{ fontSize: '0.85rem', color: '#333', marginBottom: '20px', lineHeight: '1.6', padding: '12px', border: '1px solid #dee2e6', borderTop: 'none' }}>
                  <p className="mb-2">Below is a summary of your answers. You can review your questions in three (3) different ways.</p>
                  <p className="mb-2">The buttons in the lower right-hand corner correspond to these choices:</p>
                  <p className="mb-1">1. Review all of your questions and answers.</p>
                  <p className="mb-1">2. Review questions that are incomplete.</p>
                  <p className="mb-2">3. Review questions that are flagged for review. (Click the 'flag' icon to change the flag for review status.)</p>
                  <p className="mb-0">You may also click on a question number to link directly to its location in the exam.</p>
                </div>
              )}

              {/* Questions Section */}
              <div
                style={{ backgroundColor: '#5BA4D9', color: '#ffffff', padding: '8px 12px', marginBottom: '0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setPmrQuestionsOpen(!pmrQuestionsOpen)}
              >
                <span><i className={`fas fa-${pmrQuestionsOpen ? 'minus' : 'plus'}-square mr-2`}></i>Questions</span>
                <span>({testQuestions.filter(q => !selectedAnswers[q.questionId]).length} Unseen/Incomplete)</span>
              </div>

              {/* Questions Grid */}
              {pmrQuestionsOpen && (
              <div style={{ border: '1px solid #dee2e6', borderTop: 'none' }}>
                <div className="row no-gutters">
                  {testQuestions.map((q, qIndex) => {
                    const isAnswered = selectedAnswers[q.questionId] !== undefined;
                    const isMarked = markedQuestions.includes(q.questionId);
                    return (
                      <div key={q.questionId} className="col-12 col-sm-6 col-md-4"
                        style={{
                          padding: '8px 12px',
                          borderBottom: '1px solid #dee2e6',
                          borderRight: '1px solid #dee2e6',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setShowPmrReview(false);
                          setCurrentQuestionIndex(qIndex);
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span>
                            <i
                              className={`${isMarked ? 'fas' : 'far'} fa-flag mr-2`}
                              style={{ color: isMarked ? '#1e6091' : '#ccc', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (markedQuestions.includes(q.questionId)) {
                                  setMarkedQuestions(markedQuestions.filter(id => id !== q.questionId));
                                } else {
                                  setMarkedQuestions([...markedQuestions, q.questionId]);
                                }
                                // Auto-save after flag toggle in PMR review
                                if (savedTestId && !isReviewMode) {
                                  if (answerSaveTimeoutRef.current) {
                                    clearTimeout(answerSaveTimeoutRef.current);
                                  }
                                  answerSaveTimeoutRef.current = setTimeout(() => {
                                    handleSaveProgress();
                                  }, 1000);
                                }
                              }}
                            ></i>
                            <span style={{ color: '#1e6091', cursor: 'pointer' }}>Question {qIndex + 1}</span>
                          </span>
                          {!isAnswered && <span style={{ color: '#dc3545', fontSize: '0.8rem' }}>Incomplete</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}
            </>
          ) : currentQuestion ? (
            <>
              {/* Question Text - No header, just the question */}
              <div key={`pmr-text-${currentQuestion.questionId}`} style={{ marginBottom: '20px', color: '#333', fontSize: '0.9rem', lineHeight: '1.5' }}>
                <span
                  id="question-content-pmr"
                  dangerouslySetInnerHTML={{ __html: renderQuestionText(currentQuestion.questionText) || 'Question text not available' }}
                />
              </div>

              {/* PM&R Answer Options - Normal Radio Buttons */}
              <div key={`pmr-answers-${currentQuestion.questionId}`}>
                {currentQuestion.answers && currentQuestion.answers.map((answer, index) => {
                  const isSelected = selectedAnswers[currentQuestion.questionId] === answer.answerId;
                  const isCorrectAnswer = answer.isCorrect;
                  const showResults = isReviewMode || (isTutor && showExplanation);

                  // Calculate answer choice percentage
                  const answerDistribution = currentQuestion.answerDistribution || {};
                  const totalResponses = Object.values(answerDistribution).reduce((sum, count) => sum + count, 0);
                  const chosenCount = answerDistribution[answer.answerId] || 0;
                  const chosenPercentage = totalResponses > 0 ? Math.round((chosenCount / totalResponses) * 100) : 0;

                  return (
                    <div
                      key={answer.answerId}
                      style={{ marginBottom: '10px', cursor: (showExplanation || isReviewMode) ? 'default' : 'pointer' }}
                      onClick={() => !showExplanation && !isReviewMode && handleSelectAnswer(currentQuestion.questionId, answer.answerId)}
                    >
                      <div className="d-flex align-items-start">
                        <span style={{ width: '20px', minWidth: '20px', marginTop: '2px', textAlign: 'center' }}>
                          {showResults && isCorrectAnswer && (
                            <span style={{ color: '#28a745', fontWeight: '700', fontSize: '1rem' }}>&#10003;</span>
                          )}
                          {showResults && isSelected && !isCorrectAnswer && (
                            <span style={{ color: '#dc3545', fontWeight: '700', fontSize: '1rem' }}>&#10007;</span>
                          )}
                        </span>
                        <input
                          type="radio"
                          name={`pmr-question-${currentQuestion.questionId}`}
                          checked={isSelected}
                          readOnly
                          style={{ marginRight: '6px', marginTop: '4px', cursor: (showExplanation || isReviewMode) ? 'default' : 'pointer', pointerEvents: 'none' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#333', minWidth: '24px' }}>
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <span style={{ fontSize: '0.9rem', color: '#333' }} dangerouslySetInnerHTML={{ __html: answer.answerHtml || renderChoiceText(answer.answerText) }} />
                      </div>
                      {/* Show percentage of users who chose this answer (only if 5+ responses) */}
                      {showResults && totalResponses >= 5 && (
                        <div
                          style={{
                            marginLeft: '28px',
                            marginTop: '2px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            color: isCorrectAnswer ? '#28a745' : '#6c757d'
                          }}
                        >
                          <i className="fas fa-users mr-1"></i>
                          {chosenPercentage}% chose this
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Submit Button - PM&R Style */}
              {!showExplanation && selectedAnswers[currentQuestion.questionId] && isTutor && !isReviewMode && (
                <div className="mt-4">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleSubmitAnswer}
                    style={{ backgroundColor: '#1e6091', borderColor: '#1e6091' }}
                  >
                    Submit Answer
                  </Button>
                </div>
              )}

              {/* Review Mode Result Box - PM&R Style */}
              {isReviewMode && (() => {
                const userSelectedAnswerId = selectedAnswers[currentQuestion.questionId];
                const wasOmitted = !userSelectedAnswerId;
                const selectedAnswer = currentQuestion.answers?.find(a => a.answerId === userSelectedAnswerId);
                const isCorrect = selectedAnswer?.isCorrect;
                const correctIndex = currentQuestion.answers?.findIndex(a => a.isCorrect);

                const statusColor = wasOmitted ? '#ffc107' : (isCorrect ? '#28a745' : '#dc3545');
                const statusText = wasOmitted ? 'Omitted' : (isCorrect ? 'Correct' : 'Incorrect');

                return (
                  <div className="mt-3 p-2" style={{ backgroundColor: '#f5f5f5', borderLeft: `3px solid ${statusColor}`, fontSize: '0.85rem' }}>
                    <span style={{ color: statusColor, fontWeight: '500' }}>{statusText}</span>
                    <span className="ml-2" style={{ color: '#666' }}>Correct Answer: {correctIndex >= 0 ? String.fromCharCode(65 + correctIndex) : 'N/A'}</span>
                  </div>
                );
              })()}

              {/* Tutor Mode Result Box - PM&R Style */}
              {isTutor && showExplanation && selectedAnswers[currentQuestion.questionId] && !isReviewMode && (() => {
                const selectedAnswer = currentQuestion.answers.find(a => a.answerId === selectedAnswers[currentQuestion.questionId]);
                const isCorrect = selectedAnswer?.isCorrect;
                const correctIndex = currentQuestion.answers.findIndex(a => a.isCorrect);

                const statusColor = isCorrect ? '#28a745' : '#dc3545';
                const statusText = isCorrect ? 'Correct' : 'Incorrect';

                return (
                  <div className="mt-3 p-2" style={{ backgroundColor: '#f5f5f5', borderLeft: `3px solid ${statusColor}`, fontSize: '0.85rem' }}>
                    <span style={{ color: statusColor, fontWeight: '500' }}>{statusText}</span>
                    <span className="ml-2" style={{ color: '#666' }}>Correct Answer: {String.fromCharCode(65 + correctIndex)}</span>
                  </div>
                );
              })()}

              {/* Explanation - PM&R Style */}
              {((isReviewMode && currentQuestion.explanation) || (isTutor && showExplanation && currentQuestion.explanation && !isReviewMode)) && (
                <div className="mt-3 p-2" style={{ backgroundColor: '#e8f4f8', borderLeft: '3px solid #1e6091', fontSize: '0.85rem' }}>
                  <div style={{ fontWeight: '500', color: '#1e6091', marginBottom: '4px' }}>Explanation</div>
                  <div key={`expl-pmr-${currentQuestion.questionId}`} style={{ color: '#333' }} dangerouslySetInnerHTML={{ __html: renderExplanationText(currentQuestion.explanation) }} />
                </div>
              )}

              {/* AI Tutor Chat - PM&R Layout */}
              {(showExplanation || isReviewMode) && (
                <TutoredModeAIChat
                  key={currentQuestion.questionId}
                  questionText={currentQuestion.questionText}
                  explanationText={currentQuestion.explanation}
                  answers={currentQuestion.answers}
                  correctAnswerId={currentQuestion.answers?.find(a => a.isCorrect)?.answerId}
                  userAnswerId={selectedAnswers[currentQuestion.questionId]}
                  passageText={currentQuestion.passageData?.passageText}
                  testId={savedTestId}
                  questionId={currentQuestion.questionId}
                  variant="pmr"
                />
              )}
            </>
          ) : (
            <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted">
              <i className="fas fa-spinner fa-spin fa-2x mb-2"></i>
              <p className="mb-0">Loading...</p>
            </div>
          )}
          {/* Scroll indicator - shows when answer choices are below the fold */}
          <div style={{
            position: 'sticky',
            bottom: 0,
            textAlign: 'center',
            padding: '6px 0 2px',
            opacity: canScrollDown ? 1 : 0,
            transition: 'opacity 0.3s',
            pointerEvents: 'none'
          }}>
            <i className="fas fa-angle-double-down scroll-down-indicator" style={{ fontSize: '1.3rem', color: '#adb5bd' }}></i>
          </div>
        </div>

        {/* PM&R Bottom Navigation - Dark Blue */}
        <div className="d-flex justify-content-between align-items-center px-3 flex-wrap" style={{ backgroundColor: '#1e6091', minHeight: '40px', gap: '6px', padding: '6px 16px' }}>
          {showPmrReview ? (
            <>
              {/* Left - End Review */}
              <div>
                <button
                  onClick={isReviewMode ? handleCloseTest : handleEndTest}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #ffffff',
                    color: '#ffffff',
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fas fa-sign-out-alt mr-1"></i>
                  End Review
                </button>
              </div>

              {/* Right - Review buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                <button
                  onClick={() => {
                    setShowPmrReview(false);
                    setCurrentQuestionIndex(0);
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #ffffff',
                    color: '#ffffff',
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fas fa-check-double mr-1"></i>
                  Review All
                </button>
                <button
                  onClick={() => {
                    const incompleteIndex = testQuestions.findIndex(q => !selectedAnswers[q.questionId]);
                    if (incompleteIndex >= 0) {
                      setShowPmrReview(false);
                      setCurrentQuestionIndex(incompleteIndex);
                    }
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #ffffff',
                    color: '#ffffff',
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fas fa-times mr-1"></i>
                  <span className="d-none d-sm-inline">Review </span>Incomplete
                </button>
                <button
                  onClick={() => {
                    const flaggedIndex = testQuestions.findIndex(q => markedQuestions.includes(q.questionId));
                    if (flaggedIndex >= 0) {
                      setShowPmrReview(false);
                      setCurrentQuestionIndex(flaggedIndex);
                    }
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #ffffff',
                    color: '#ffffff',
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fas fa-flag mr-1"></i>
                  <span className="d-none d-sm-inline">Review </span>Flagged
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Left side - Review Screen button (show after user has seen review OR if test is submitted) */}
              <div>
                {(hasSeenPmrReview || isReviewMode) && (
                  <button
                    onClick={() => setShowPmrReview(true)}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ffffff',
                      color: '#ffffff',
                      padding: '4px 12px',
                      fontSize: '0.8rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-sign-out-alt mr-1"></i>
                    Review Screen
                  </button>
                )}
              </div>

              {/* Right - Previous and Next buttons */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => {
                    if (showPmrReview) {
                      setShowPmrReview(false);
                    } else {
                      handlePrevQuestion();
                    }
                  }}
                  disabled={currentQuestionIndex === 0 && !showPmrReview}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #ffffff',
                    color: '#ffffff',
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: (currentQuestionIndex === 0 && !showPmrReview) ? 'not-allowed' : 'pointer',
                    opacity: (currentQuestionIndex === 0 && !showPmrReview) ? 0.5 : 1
                  }}
                >
                  <i className="fas fa-chevron-left mr-1"></i>
                  Previous
                </button>
                <button
                  onClick={() => {
                    if (currentQuestionIndex === testQuestions.length - 1) {
                      setShowPmrReview(true);
                      setHasSeenPmrReview(true);
                    } else {
                      handleNextQuestion();
                    }
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #ffffff',
                    color: '#ffffff',
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  Next
                  <i className="fas fa-chevron-right ml-1"></i>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      )}
    </Modal>

    {/* Question Navigator Modal */}
    <Modal
      isOpen={isNavigatorModalOpen}
      toggle={() => setIsNavigatorModalOpen(false)}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalHeader toggle={() => setIsNavigatorModalOpen(false)}>
        Navigator - select a question to go to it
      </ModalHeader>
      <ModalBody className="p-1" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <table className="table table-bordered table-hover mb-0">
          <thead className="bg-primary">
            <tr>
              <th className="py-2 text-center text-white">Question #</th>
              <th className="py-2 text-center text-white">Status</th>
              {/* Result is tutor-mode only — in timed mode nothing is submitted
                  until the end, so showing it would give away the answers */}
              {isTutor && <th className="py-2 text-center text-white">Result</th>}
              <th className="py-2 text-center text-white">Flag for Review</th>
            </tr>
          </thead>
          <tbody>
            {testQuestions.map((q, index) => {
              const isAnswered = selectedAnswers[q.questionId] !== undefined;
              const isMarked = markedQuestions.includes(q.questionId);
              const isCurrent = index === currentQuestionIndex;
              const isSubmitted = submittedQuestions.includes(q.questionId);
              const correctAnswerId = q.answers?.find(a => a.isCorrect)?.answerId;
              const isCorrect = selectedAnswers[q.questionId] === correctAnswerId;

              return (
                <tr
                  key={q.questionId}
                  style={{ backgroundColor: isCurrent ? '#e9ecef' : 'transparent', cursor: 'pointer' }}
                  onClick={() => {
                    handleGoToQuestion(index);
                    setIsNavigatorModalOpen(false);
                  }}
                  onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                  onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <td className="py-2 text-dark text-center">
                    <span className="font-weight-bold">Question {index + 1}</span>
                  </td>
                  <td className="py-2 text-center">
                    {!isAnswered ? (
                      <Badge color="danger" pill>Incomplete</Badge>
                    ) : (
                      <Badge color="success" pill>Complete</Badge>
                    )}
                  </td>
                  {isTutor && (
                    <td className="py-2 text-center">
                      {!isSubmitted ? (
                        <span className="text-muted">&mdash;</span>
                      ) : isCorrect ? (
                        <Badge color="success" pill>
                          <i className="fas fa-check mr-1"></i>Correct
                        </Badge>
                      ) : (
                        <Badge color="danger" pill>
                          <i className="fas fa-times mr-1"></i>Incorrect
                        </Badge>
                      )}
                    </td>
                  )}
                  <td className="py-2 text-center">
                    {isMarked && <i className="fas fa-flag text-warning" style={{ fontSize: '1rem' }}></i>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ModalBody>
      <ModalFooter className="py-2 d-flex justify-content-between align-items-center">
        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
          {(() => {
            const incompleteCount = testQuestions.filter(q => !selectedAnswers[q.questionId]).length;
            if (!isTutor) return `${incompleteCount} Incomplete`;

            // Only graded questions count — unsubmitted ones have no result yet
            const graded = testQuestions.filter(q => submittedQuestions.includes(q.questionId));
            const correctCount = graded.filter(
              q => selectedAnswers[q.questionId] === q.answers?.find(a => a.isCorrect)?.answerId
            ).length;

            return `${correctCount} Correct · ${graded.length - correctCount} Incorrect · ${incompleteCount} Incomplete`;
          })()}
        </span>
        <Button color="secondary"  onClick={() => setIsNavigatorModalOpen(false)}>
          <i className="fas fa-times mr-1"></i>
          Close
        </Button>
      </ModalFooter>
    </Modal>

    {/* Idle Detection Modal */}
    <Modal
      isOpen={isIdleModalOpen}
      className="modal-dialog-centered"
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader>
        <i className="fas fa-pause-circle text-warning mr-2"></i>
        Test Paused
      </ModalHeader>
      <ModalBody className="text-center py-4">
        <i className={`fas ${pauseReason === 'tab' ? 'fa-window-restore' : 'fa-clock'} text-muted mb-3`} style={{ fontSize: '3rem' }}></i>
        <p className="mb-2">
          {pauseReason === 'tab'
            ? "You left this tab, so we've paused the timer to keep your time accurate."
            : "You've been inactive for 5 minutes, so we've paused the timer to keep your time accurate."
          }
        </p>
        <p className="text-muted mb-0">
          Click the button below to resume your test.
        </p>
      </ModalBody>
      <ModalFooter className="justify-content-center">
        <Button color="primary" size="lg" onClick={handleResumeFromIdle}>
          <i className="fas fa-play mr-2"></i>
          Resume Test
        </Button>
      </ModalFooter>
    </Modal>

    {/* Periodic Table Modal */}
    <Modal
      isOpen={isPeriodicTableOpen}
      toggle={() => setIsPeriodicTableOpen(false)}
      size="xl"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={() => setIsPeriodicTableOpen(false)}>
        Periodic Table of Elements
      </ModalHeader>
      <ModalBody className="p-2" style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '1000px', fontSize: '0.75rem' }}>
          <table className="w-100" style={{ borderCollapse: 'separate', borderSpacing: '1px', tableLayout: 'fixed' }}>
            <tbody>
              {/* Row 1 */}
              <tr>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#f5365c' }}><strong>1</strong><br/>H<br/>1.008</td>
                <td colSpan="16"></td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#11cdef' }}><strong>2</strong><br/>He<br/>4.003</td>
              </tr>
              {/* Row 2 */}
              <tr>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>3</strong><br/>Li<br/>6.941</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>4</strong><br/>Be<br/>9.012</td>
                <td colSpan="10"></td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>5</strong><br/>B<br/>10.81</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#f5365c' }}><strong>6</strong><br/>C<br/>12.01</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#f5365c' }}><strong>7</strong><br/>N<br/>14.01</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#f5365c' }}><strong>8</strong><br/>O<br/>16.00</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#8965e0' }}><strong>9</strong><br/>F<br/>19.00</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#11cdef' }}><strong>10</strong><br/>Ne<br/>20.18</td>
              </tr>
              {/* Row 3 */}
              <tr>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>11</strong><br/>Na<br/>22.99</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>12</strong><br/>Mg<br/>24.31</td>
                <td colSpan="10"></td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>13</strong><br/>Al<br/>26.98</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>14</strong><br/>Si<br/>28.09</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#f5365c' }}><strong>15</strong><br/>P<br/>30.97</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#f5365c' }}><strong>16</strong><br/>S<br/>32.07</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#8965e0' }}><strong>17</strong><br/>Cl<br/>35.45</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#11cdef' }}><strong>18</strong><br/>Ar<br/>39.95</td>
              </tr>
              {/* Row 4 */}
              <tr>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>19</strong><br/>K<br/>39.10</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>20</strong><br/>Ca<br/>40.08</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>21</strong><br/>Sc<br/>44.96</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>22</strong><br/>Ti<br/>47.87</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>23</strong><br/>V<br/>50.94</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>24</strong><br/>Cr<br/>52.00</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>25</strong><br/>Mn<br/>54.94</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>26</strong><br/>Fe<br/>55.85</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>27</strong><br/>Co<br/>58.93</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>28</strong><br/>Ni<br/>58.69</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>29</strong><br/>Cu<br/>63.55</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>30</strong><br/>Zn<br/>65.38</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>31</strong><br/>Ga<br/>69.72</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>32</strong><br/>Ge<br/>72.63</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>33</strong><br/>As<br/>74.92</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>34</strong><br/>Se<br/>78.97</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#8965e0' }}><strong>35</strong><br/>Br<br/>79.90</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#11cdef' }}><strong>36</strong><br/>Kr<br/>83.80</td>
              </tr>
              {/* Row 5 */}
              <tr>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>37</strong><br/>Rb<br/>85.47</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>38</strong><br/>Sr<br/>87.62</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>39</strong><br/>Y<br/>88.91</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>40</strong><br/>Zr<br/>91.22</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>41</strong><br/>Nb<br/>92.91</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>42</strong><br/>Mo<br/>95.95</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>43</strong><br/>Tc<br/>(98)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>44</strong><br/>Ru<br/>101.1</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>45</strong><br/>Rh<br/>102.9</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>46</strong><br/>Pd<br/>106.4</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>47</strong><br/>Ag<br/>107.9</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>48</strong><br/>Cd<br/>112.4</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>49</strong><br/>In<br/>114.8</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>50</strong><br/>Sn<br/>118.7</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>51</strong><br/>Sb<br/>121.8</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>52</strong><br/>Te<br/>127.6</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#8965e0' }}><strong>53</strong><br/>I<br/>126.9</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#11cdef' }}><strong>54</strong><br/>Xe<br/>131.3</td>
              </tr>
              {/* Row 6 */}
              <tr>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>55</strong><br/>Cs<br/>132.9</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>56</strong><br/>Ba<br/>137.3</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#ffd600' }}><strong>57-71</strong><br/>La-Lu<br/></td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>72</strong><br/>Hf<br/>178.5</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>73</strong><br/>Ta<br/>180.9</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>74</strong><br/>W<br/>183.8</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>75</strong><br/>Re<br/>186.2</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>76</strong><br/>Os<br/>190.2</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>77</strong><br/>Ir<br/>192.2</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>78</strong><br/>Pt<br/>195.1</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>79</strong><br/>Au<br/>197.0</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>80</strong><br/>Hg<br/>200.6</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>81</strong><br/>Tl<br/>204.4</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>82</strong><br/>Pb<br/>207.2</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>83</strong><br/>Bi<br/>209.0</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#2dce89' }}><strong>84</strong><br/>Po<br/>(209)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#8965e0' }}><strong>85</strong><br/>At<br/>(210)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#11cdef' }}><strong>86</strong><br/>Rn<br/>(222)</td>
              </tr>
              {/* Row 7 */}
              <tr>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>87</strong><br/>Fr<br/>(223)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#fb6340' }}><strong>88</strong><br/>Ra<br/>(226)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5603ad' }}><strong>89-103</strong><br/>Ac-Lr<br/></td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>104</strong><br/>Rf<br/>(267)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>105</strong><br/>Db<br/>(268)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>106</strong><br/>Sg<br/>(269)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>107</strong><br/>Bh<br/>(270)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>108</strong><br/>Hs<br/>(277)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>109</strong><br/>Mt<br/>(278)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>110</strong><br/>Ds<br/>(281)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>111</strong><br/>Rg<br/>(282)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#5e72e4' }}><strong>112</strong><br/>Cn<br/>(285)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>113</strong><br/>Nh<br/>(286)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>114</strong><br/>Fl<br/>(289)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>115</strong><br/>Mc<br/>(290)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#32325d' }}><strong>116</strong><br/>Lv<br/>(293)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#8965e0' }}><strong>117</strong><br/>Ts<br/>(294)</td>
                <td className="text-center p-1 border text-white" style={{ backgroundColor: '#11cdef' }}><strong>118</strong><br/>Og<br/>(294)</td>
              </tr>
            </tbody>
          </table>
          {/* Legend */}
          <div className="d-flex flex-wrap justify-content-center mt-2" style={{ fontSize: '0.55rem' }}>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#fb6340' }}></span>Alkali/Alkaline</span>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#5e72e4' }}></span>Transition</span>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#32325d' }}></span>Post-transition</span>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#2dce89' }}></span>Metalloid</span>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#f5365c' }}></span>Nonmetal</span>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#8965e0' }}></span>Halogen</span>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#11cdef' }}></span>Noble Gas</span>
            <span className="mr-2"><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#ffd600' }}></span>Lanthanide</span>
            <span><span className="d-inline-block mr-1 border" style={{ width: '10px', height: '10px', backgroundColor: '#5603ad' }}></span>Actinide</span>
          </div>
        </div>
      </ModalBody>
    </Modal>

    {/* Test Report/Review Modal */}
    <Modal
      isOpen={isReportModalOpen}
      toggle={handleCloseReportModal}
      size="lg"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={handleCloseReportModal} className="border-bottom">
        <div className="d-flex align-items-center justify-content-between w-100">
          <h5 className="mb-0 font-weight-bold">Section Review</h5>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }} className="p-0">
        {/* Tabs */}
        <div className="d-flex flex-wrap p-3 gap-2" style={{ gap: '8px' }}>
          <Button
            color={!reviewFilter || reviewFilter === 'all' ? 'primary' : 'secondary'}
            outline={reviewFilter !== 'all' && reviewFilter}
            size="sm"
            onClick={() => setReviewFilter('all')}
          >
            All <Badge color="light" className="ml-1 text-dark">{testQuestions.length}</Badge>
          </Button>
          <Button
            color={reviewFilter === 'incomplete' ? 'danger' : 'secondary'}
            outline={reviewFilter !== 'incomplete'}
            size="sm"
            onClick={() => setReviewFilter('incomplete')}
          >
            Incomplete <Badge color="light" className="ml-1 text-dark">{testQuestions.length - Object.keys(selectedAnswers).length}</Badge>
          </Button>
          <Button
            color={reviewFilter === 'flagged' ? 'info' : 'secondary'}
            outline={reviewFilter !== 'flagged'}
            size="sm"
            onClick={() => setReviewFilter('flagged')}
          >
            Flagged <Badge color="light" className="ml-1 text-dark">{markedQuestions.length}</Badge>
          </Button>
        </div>

        {/* Table */}
        <div className="p-1">
          <table className="table table-bordered table-hover mb-0">
            <thead className="bg-primary">
              <tr>
                <th className="py-2 text-center text-white">Question #</th>
                <th className="py-2 text-center text-white">Status</th>
                <th className="py-2 text-center text-white">Flag for Review</th>
                <th className="py-2 text-center text-white">Action</th>
              </tr>
            </thead>
            <tbody>
              {testQuestions
                .map((question, idx) => ({ question, idx }))
                .filter(({ question }) => {
                  const isAnswered = selectedAnswers[question.questionId] !== undefined;
                  const isMarked = markedQuestions.includes(question.questionId);
                  if (reviewFilter === 'incomplete') return !isAnswered;
                  if (reviewFilter === 'flagged') return isMarked;
                  return true;
                })
                .map(({ question, idx }) => {
                  const selectedAnswerId = selectedAnswers[question.questionId];
                  const isAnswered = selectedAnswerId !== undefined;
                  const isMarked = markedQuestions.includes(question.questionId);

                  return (
                    <tr
                      key={question.questionId}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                      onClick={() => handleReviewQuestion(idx)}
                    >
                      <td className="py-2 text-dark text-center">
                        <span className="font-weight-bold">Question {idx + 1}</span>
                      </td>
                      <td className="py-2 text-center">
                        {!isAnswered ? (
                          <Badge color="danger" pill>Incomplete</Badge>
                        ) : (
                          <Badge color="success" pill>Complete</Badge>
                        )}
                      </td>
                      <td className="py-2 text-center">
                        {isMarked && <i className="fas fa-flag text-warning" style={{ fontSize: '1rem' }}></i>}
                      </td>
                      <td className="py-2 text-center">
                        <span
                          className="text-primary font-weight-bold"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReviewQuestion(idx);
                          }}
                        >
                          Review
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <Button color="secondary" onClick={handleCloseReportModal}>
          <i className="fas fa-arrow-left mr-2"></i>
          Continue Test
        </Button>
        <Button color="success" onClick={handleSubmitTest}>
          <i className="fas fa-stop mr-2"></i>
          End Test
        </Button>
      </ModalFooter>
    </Modal>

    {/* Question Feedback Modal */}
    <QuestionFeedbackModal
      isOpen={isFeedbackModalOpen}
      toggle={() => setIsFeedbackModalOpen(false)}
      questionId={currentQuestion?.questionId}
      questionCode={currentQuestion?.questionCode}
      passageId={currentQuestion?.passageData?.passageId}
      testId={savedTestId}
      layoutMode={testLayoutMode}
      source="test"
    />
    </AccessRestriction>
    </>
  );
};

export default CreateTest;
